// =============================================================================
// IMPORTS (combined from all files)
// =============================================================================
import { useState, useRef, useEffect, useCallback } from "react";
import { generateReceiptHTML } from "../Esim/Edata.js";
import receiptLogo from "../../../../assets/PNG/RecieptLogo.png";
import { getDocuments, getTotalWalletBalance } from "../../Users/Auth.js";
import { useAuth } from "../../../../context/AuthContext";
import { COUNTRY_CODES, getWallet, saveWallet, spendRewardMoney } from "../../Users/ProfileLogic.js";
import passportService from "../../../../services/passportService";
import configService from "../../../../services/configService";
import "./Passportguide.css";
import Login from "../../Users/Login";
import PaymentForm, {
  PAYMENT_DETAILS_DEFAULT,
  validatePaymentDetails,
  PAYMENT_METHODS_LIST,
  RewardDiscountSection,
  getCurrentWalletBalances,
  REWARD_DISCOUNT_MAX_RATE,
} from "../../../Common/PaymentForm/PaymentForm";

// =============================================================================
// Applicationstore.js - In-memory application store
// =============================================================================
const applicationStore = {};

export function saveApplication(trackId, data) {
  applicationStore[trackId.toUpperCase()] = data;
}

export function getApplication(trackId) {
  return applicationStore[trackId.toUpperCase()] || null;
}

export async function savePassportBooking(booking, userEmail) {
  const email = userEmail || booking?.email;
  if (!email) return;
  await passportService.book({ ...booking, email }).catch(() => {});
}

export async function fetchPassportBookings(userEmail) {
  if (!userEmail) return [];
  try {
    const result = await passportService.list(userEmail);
    return result.data || [];
  } catch {
    return [];
  }
}

// =============================================================================
// ApplicationForm.js - Application form component, helpers, and constants
// =============================================================================


let SERVICE_OPTIONS = [];
configService.getConfig().then((r) => {
  const passportOptions = Array.isArray(r?.data?.passportServiceOptions) ? r.data.passportServiceOptions : [];
  SERVICE_OPTIONS = passportOptions;
  REAPPLY_SERVICE_OPTIONS = passportOptions.map((item) => ({
    id: item.id,
    label: item.label,
    fee: item.serviceFee || 0,
  }));
}).catch(() => {});


const EMPTY_APPLICANT = () => ({
  firstName: "", lastName: "", phone: "", phoneCode: "+91", idNumber: "", emergencyContact: "", emergencyCode: "+91", country: "",
  address: "", postalCode: "",
  documents: { aadhaar_front: null, aadhaar_back: null, passport_front: null, passport_back: null, pan_front: null, pan_back: null },
});

function generateAppId() {
  const prefix = "TVC";
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${ts}-${rand}`;
}

function generateTrackId() {
  return "TRK-" + Math.random().toString(36).substring(2, 10).toUpperCase();
}

function AppLoginGate({ open, onClose, onSuccess }) {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  function handleLoginSuccess(user) {
    onSuccess(user);
    onClose();
  }

  return (
    <div
      className="lg-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="lg-inner">
        <button className="lg-close" onClick={onClose}>✕</button>
        <Login onLoginSuccess={handleLoginSuccess} />
      </div>
    </div>
  );
}

function deductWalletDiscount(email, discountAmount, description, totalAmount) {
  if (!email || discountAmount <= 0) return { success: false };
  const wallet = getWallet(email);
  const totalBalance = (wallet.redeemable?.balance || 0) + (wallet.nonRedeemable?.balance || 0);
  if (totalBalance < discountAmount) return { success: false, wallet };
  
  let remaining = discountAmount;
  
  const redeemableUsed = Math.min(wallet.redeemable?.balance || 0, remaining);
  if (redeemableUsed > 0) {
    wallet.redeemable.balance -= redeemableUsed;
    remaining -= redeemableUsed;
    wallet.redeemable.transactions = wallet.redeemable.transactions || [];
    wallet.redeemable.transactions.unshift({
      id: `discount_${Date.now()}_r`,
      label: description || "Reward Discount Applied",
      amount: -redeemableUsed,
      type: "debit",
      date: new Date().toISOString(),
      note: "Applied as discount on passport application service fee",
      totalPrice: totalAmount || 0,
    });
  }
  
  if (remaining > 0) {
    const nonRedeemableUsed = Math.min(wallet.nonRedeemable?.balance || 0, remaining);
    if (nonRedeemableUsed > 0) {
      wallet.nonRedeemable.balance -= nonRedeemableUsed;
      wallet.nonRedeemable.transactions = wallet.nonRedeemable.transactions || [];
      wallet.nonRedeemable.transactions.unshift({
        id: `discount_${Date.now()}_nr`,
        label: description || "Reward Discount Applied",
        amount: -nonRedeemableUsed,
        type: "debit",
        date: new Date().toISOString(),
        note: "Applied as discount on passport application service fee",
        totalPrice: totalAmount || 0,
      });
    }
  }
  
  saveWallet(email, wallet);
  return { success: true, wallet };
}

export function ApplicationForm() {
  const [step, setStep]                     = useState(1);
  const [service, setService]               = useState("");
  const [passportBuddy, setPassportBuddy]   = useState(false);
  const [numApplicants, setNumApplicants]   = useState(1);
  const [applicants, setApplicants]         = useState([EMPTY_APPLICANT()]);
  const [paymentMode, setPaymentMode]       = useState("debit");
  const [paymentDetails, setPaymentDetails] = useState(PAYMENT_DETAILS_DEFAULT);
  const setPayF = (e) => setPaymentDetails(d => ({ ...d, [e.target.name]: e.target.value }));
  const [consentChecked, setConsentChecked] = useState(false);
  const [errors, setErrors]                 = useState([]);
  const [appData, setAppData]               = useState(null);
  const [currentApplicant, setCurrentApplicant] = useState(0);
  const [slideDir, setSlideDir]               = useState("af-slide-in-left");
  const [showLoginGate, setShowLoginGate]     = useState(false);
  const [rewardDiscountAmount, setRewardDiscountAmount] = useState(0);

  const { user: session } = useAuth();
  const isLoggedIn = !!session?.email;
  const walletBalances = isLoggedIn ? getCurrentWalletBalances(session?.email) : null;
  const selectedService = SERVICE_OPTIONS.find((s) => s.id === service);
  const maxRewardDiscount = Math.round((selectedService ? selectedService.serviceFee * numApplicants : 0) * REWARD_DISCOUNT_MAX_RATE);
  const payMethodObj    = PAYMENT_METHODS_LIST.find(m => m.id === paymentMode) || PAYMENT_METHODS_LIST[0];

  const buddyFee        = passportBuddy ? 1999 : 0;
  const serviceFeeTotal = selectedService ? selectedService.serviceFee * numApplicants : 0;
  const govtFeeTotal    = selectedService ? selectedService.govtFee * numApplicants : 0;
  const subtotal        = serviceFeeTotal + buddyFee;
  const gst             = Math.round(subtotal * 0.18);
  const convFee         = payMethodObj.fee;
  const total           = subtotal + gst + convFee - rewardDiscountAmount;
  const grandTotal      = total + govtFeeTotal;

  const updateApplicant = (idx, field, value) => {
    setApplicants((prev) => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const updateApplicantDoc = (idx, docKey, file) => {
    if (file === null) {
      setApplicants((prev) =>
        prev.map((a, i) =>
          i === idx ? { ...a, documents: { ...a.documents, [docKey]: null } } : a
        )
      );
      return;
    }
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setApplicants((prev) =>
        prev.map((a, i) =>
          i === idx ? { ...a, documents: { ...a.documents, [docKey]: { name: file.name, url: e.target.result, size: file.size } } } : a
        )
      );
    };
    reader.readAsDataURL(file);
  };

  const handleNumApplicantsChange = (n) => {
    const num = Math.max(1, Math.min(10, n));
    setNumApplicants(num);
    setApplicants((prev) => {
      const arr = [...prev];
      while (arr.length < num) arr.push(EMPTY_APPLICANT());
      return arr.slice(0, num);
    });
    if (currentApplicant >= num) {
      setCurrentApplicant(num - 1);
    }
  };

  const validateForm = () => {
    return applicants.map((a) => {
      const e = {};
      if (!a.firstName.trim()) e.firstName = "First name is required.";
      if (!a.phone.trim() || a.phone.trim().length < 4) e.phone = "Enter a valid phone number.";
      if (!a.idNumber.trim()) e.idNumber = "ID number (Aadhaar/PAN) is required.";
      if (!a.emergencyContact.trim() || a.emergencyContact.trim().length < 4) e.emergencyContact = "Valid emergency contact required.";
      if (!a.address.trim()) e.address = "Address is required.";
      if (!/^\d{6}$/.test(a.postalCode.trim())) e.postalCode = "Enter a valid 6-digit postal code.";
      return e;
    });
  };

  const handleFormNext = () => {
    if (!service) { alert("Please select a service to avail."); return; }
    const errs = validateForm();
    if (errs.some((e) => Object.keys(e).length > 0)) { setErrors(errs); return; }
    setErrors([]);
    setStep(2);
  };

  const handleNextApplicant = () => {
    if (currentApplicant < numApplicants - 1) {
      setSlideDir("af-slide-in-left");
      setCurrentApplicant((c) => c + 1);
    }
  };

  const handlePrevApplicant = () => {
    if (currentApplicant > 0) {
      setSlideDir("af-slide-in-right");
      setCurrentApplicant((c) => c - 1);
    }
  };

  const handleConsentNext = () => {
    if (!consentChecked) return; // button is disabled anyway
    setStep(3);
  };

  const handlePayment = () => {
    if (paymentMode === "reward") {
      if (!isLoggedIn || !session?.email) {
        alert("Please sign in to use Pay by Reward.");
        return;
      }
      if (walletBalances && grandTotal > walletBalances.total) {
        alert(`Insufficient wallet balance. You need ₹${grandTotal.toLocaleString("en-IN")} but have ₹${walletBalances.total.toLocaleString("en-IN")}.`);
        return;
      }
      const result = spendRewardMoney(session.email, grandTotal, "Passport Application — Pay by Reward");
      if (!result.success) {
        alert("Failed to deduct reward balance. Please try again.");
        return;
      }
    } else {
      if (rewardDiscountAmount > 0) {
        if (!isLoggedIn) {
          alert("Please sign in to use reward discounts.");
          return;
        }
        if (rewardDiscountAmount > maxRewardDiscount) {
          alert(`Discount cannot exceed ${Math.round(REWARD_DISCOUNT_MAX_RATE * 100)}% of the application fee (₹${maxRewardDiscount.toLocaleString("en-IN")}).`);
          return;
        }
        if (walletBalances && rewardDiscountAmount > walletBalances.total) {
          alert(`Insufficient wallet balance. You have ₹${walletBalances.total.toLocaleString("en-IN")} but trying to use ₹${rewardDiscountAmount.toLocaleString("en-IN")}.`);
          return;
        }
      }

      const err = validatePaymentDetails(paymentMode, paymentDetails);
      if (err) { alert(err); return; }

      if (rewardDiscountAmount > 0 && session?.email) {
        deductWalletDiscount(session.email, rewardDiscountAmount, "Passport Application Discount", grandTotal);
      }
    }

    const applicationId = generateAppId();
    const trackId       = generateTrackId();
    const now           = new Date();

    const data = {
      applicationId,
      trackId,
      service: selectedService,
      passportBuddy,
      numApplicants,
      applicants,
      paymentMode: paymentMode === "reward" ? "Pay by Reward" : payMethodObj,
      rewardUsed: paymentMode === "reward" ? grandTotal : rewardDiscountAmount,
      serviceFeeTotal,
      govtFeeTotal,
      buddyFee,
      subtotal,
      gst,
      convFee,
      rewardDiscount: paymentMode === "reward" ? grandTotal : rewardDiscountAmount,
      total,
      grandTotal,
      submittedAt: now.toLocaleString("en-IN"),
      status: "appointment_booked",
      serviceLabel: selectedService.label,
      appointmentDate: "To be confirmed within 24 hours",
      appointmentTime: "To be confirmed",
      appointmentCenter: "Nearest PSK / POPSK to your address",
      filingStatus: "Received — Processing Begins",
      filedAt: "Pending",
      policeVerification: "Pending",
      dispatchStatus: "Awaiting appointment and biometric capture",
      stepIndex: 1,
      upiId: paymentDetails.upiId || "",
    };

    setAppData(data);
    saveApplication(trackId, data);
    savePassportBooking({
      id: applicationId,
      type: "passport-application",
      paymentMethod: typeof data.paymentMode === "object" ? data.paymentMode?.label : data.paymentMode,
      totalAmount: grandTotal,
      rewardDeduction: paymentMode === "reward" ? grandTotal : (rewardDiscountAmount || 0),
      bookedAt: now.toISOString(),
    }, session?.email);
    setStep(4);
  };

  const handleReset = () => {
    setStep(1); setService(""); setPassportBuddy(false);
    setNumApplicants(1); setApplicants([EMPTY_APPLICANT()]);
    setConsentChecked(false); setPaymentMode("debit"); setPaymentDetails(PAYMENT_DETAILS_DEFAULT);
    setAppData(null); setErrors([]); setRewardDiscountAmount(0);
  };

  if (step === 4 && appData) {
    return <SuccessScreen appData={appData} onReset={handleReset} />;
  }

  /* ── STEP 3 — Payment ── */
  if (step === 3) {
    return (
      <div className="af-root">
        <div className="af-container">
          <div className="af-header">
            <h2 className="af-title">Payment Summary</h2>
            <p className="af-sub">Review your order and complete payment to confirm your application.</p>
          </div>
          <div className="af-payment-grid">
            <div className="af-summary-card">
              <h3 className="af-section-label">Application Summary</h3>
              {/* <div className="af-billto-section">
                <div className="af-billto-title">Bill To — Applicant Details</div>
                <div className="af-billto-content">
                  {applicants.map((a, i) => (
                    <span key={i}>
                      <strong>{a.firstName} {a.lastName}</strong><br />
                      Individual Account<br />
                      {a.phoneCode} {a.phone}<br />
                      {a.address}{a.postalCode ? ` \u2013 ${a.postalCode}` : ""}
                      {i < applicants.length - 1 && <br />}
                    </span>
                  ))}
                </div>
              </div> */}
              <div className="af-summary-rows">
                <div className="af-summary-row" style={{ justifyContent: "center", flexWrap: "nowrap" }}><span style={{ fontWeight: 600, textAlign: "center" }}>{selectedService?.label}</span></div>
                <div className="af-summary-row"><span>Applicants</span><strong>{numApplicants}</strong></div>
                <div className="af-summary-row"><span>Passport Buddy</span><strong>{passportBuddy ? "Yes — Included" : "Not Availed"}</strong></div>
                <div className="af-summary-divider" />
                <div className="af-summary-row"><span>Payment Method</span><strong>{payMethodObj.label}</strong></div>
                <div className="af-summary-row"><span>Govt. Fee ({numApplicants}x)</span><strong>Rs. {govtFeeTotal.toLocaleString("en-IN")}</strong></div>
                <div className="af-summary-row"><span>Service Fee ({numApplicants}x)</span><strong>Rs. {serviceFeeTotal.toLocaleString("en-IN")}</strong></div>
                <div className="af-summary-row"><span>GST (18%)</span><strong>Rs. {gst.toLocaleString("en-IN")}</strong></div>
                <div className="af-summary-row"><span>Convenience Fee</span><strong>{convFee === 0 ? "Free" : `Rs. ${convFee}`}</strong></div>
                {paymentMode !== "reward" && rewardDiscountAmount > 0 && (
                  <div className="af-summary-row"><span>Discount Applied</span><strong style={{ color: "#16a34a" }}>− Rs. {rewardDiscountAmount.toLocaleString("en-IN")}</strong></div>
                )}
                {passportBuddy && <div className="af-summary-row"><span>Passport Buddy Fee</span><strong>Rs. {buddyFee.toLocaleString("en-IN")}</strong></div>}
                {paymentMode === "upi" && paymentDetails.upiId && <div className="af-summary-row"><span>UPI ID</span><strong>{paymentDetails.upiId}</strong></div>}
                {paymentMode === "reward" ? (
                  <>
                    <div className="af-summary-row"><span>Pay by Reward</span><strong style={{ color: "#16a34a" }}>− Rs. {grandTotal.toLocaleString("en-IN")}</strong></div>
                    <div className="af-summary-divider" />
                    <div className="af-summary-row af-summary-row--total"><span>Total</span><strong>Rs. 0</strong></div>
                  </>
                ) : (
                  <>
                    <div className="af-summary-divider" />
                    <div className="af-summary-row af-summary-row--total"><span>Total</span><strong>Rs. {grandTotal.toLocaleString("en-IN")}</strong></div>
                  </>
                )}
              </div>
            </div>
             <div className="af-payment-card">
               <PaymentForm
                 selectedMethod={paymentMode}
                 onMethodSelect={(id) => { setPaymentMode(id); setPaymentDetails(PAYMENT_DETAILS_DEFAULT); }}
                 details={paymentDetails}
                 onDetailChange={setPayF}
                 walletBalances={walletBalances}
                  requiredAmount={grandTotal}
                 onRewardPay={handlePayment}
               />

               <RewardDiscountSection
                 selectedMethod={paymentMode}
                 baseApplicationFee={serviceFeeTotal}
                 walletBalances={walletBalances}
                 discountAmount={rewardDiscountAmount}
                 onDiscountChange={setRewardDiscountAmount}
                 onOpenLogin={() => { setShowLoginGate(true); setRewardDiscountAmount(0); }}
               />

               {/* <div className="af-payment-note">
                 <p>Your payment is secured. After payment, you will receive your unique Application ID and Track ID — use it to track your application status.</p>
               </div> */}
               <div className="af-payment-actions">
                  <button className="pg-btn-primary af-pay-btn pg-btn-lg" onClick={handlePayment} disabled={!paymentMode}>
                    Pay NOW
                  </button>
                 <button className="pb-cancel-btn af-back-btn" onClick={() => setStep(2)}>Back</button>
               </div>
             </div>
           </div>
         </div>

         <AppLoginGate
           open={showLoginGate}
           onClose={() => setShowLoginGate(false)}
           onSuccess={() => {}}
         />
       </div>
     );
   }

  /* ── STEP 2 — Consent ── */
  if (step === 2) {
    return (
      <div className="af-root">
        <div className="af-container">
          <div className="af-header">
            <h2 className="af-title">Consent &amp; Agreement</h2>
            <p className="af-sub">Please read the following terms carefully before proceeding.</p>
          </div>

          <div className="af-consent-box">
            <div className="af-consent-scroll">
              <h4>Consent &amp; Agreement — TheVisaCo</h4>
              <p>By proceeding with this application, you ("User") agree to the following terms and conditions for passport renewal, Tatkal services, appointment booking, and related assistance provided by TheVisaCo.</p>
              <h5>1. User Consent for Information Collection</h5>
              <p>You agree to provide accurate, complete, and up-to-date personal information including but not limited to: full name, date of birth, and contact details; address and identity details; passport-related information; supporting documents (uploaded digitally).</p>
              <h5>2. Document Upload &amp; Verification</h5>
              <p>By uploading documents, you confirm that: all documents are genuine and belong to you; you have the legal right to share these documents; any incorrect or fraudulent document submission is solely your responsibility.</p>
              <h5>3. Service Scope</h5>
              <p>Our services include: assistance with passport renewal and Tatkal applications; appointment checking and booking at passport centers; application form filling and review; general guidance throughout the process.</p>
              <h5>4. Appointment Booking</h5>
              <p>Appointment availability depends on official passport systems and cannot be guaranteed. We will make best efforts to secure the earliest available slot. Any delays or rescheduling by authorities are beyond our control.</p>
              <h5>5. Payment Terms</h5>
              <p>All service fees must be paid in advance. Fees include service charges and may exclude government fees unless specified. Payments once made are non-refundable under any circumstances.</p>
              <h5>6. No Refund Policy</h5>
              <p>No refunds will be provided for: application rejection; delays from government authorities; incorrect information provided by the user; change of mind after payment.</p>
              <h5>7. Liability Limitation</h5>
              <p>We are not responsible for: government decisions, delays, or rejections; errors due to incorrect user-provided information; technical issues on official portals. Our role is limited to assistance and facilitation.</p>
              {passportBuddy && (
                <>
                  <h5>8. On-Ground Assistance (Passport Buddy)</h5>
                  <p>Where applicable, we provide a representative/agent to assist you during your visit to the passport center. This assistance is limited to guidance and does not influence official decisions. Our agent will accompany you through queues, counters, and document verification but has no authority over government officers' decisions.</p>
                </>
              )}
              <h5>{passportBuddy ? "9" : "8"}. User Responsibility</h5>
              <p>You agree to: attend appointments on time; carry original documents as required; follow instructions provided by authorities.</p>
              <h5>{passportBuddy ? "10" : "9"}. Privacy &amp; Data Protection</h5>
              <p>Your data will be handled securely and used only for service purposes. We do not sell or share your personal information with third parties except where required for processing your application.</p>
              <h5>{passportBuddy ? "11" : "10"}. Agreement Acceptance</h5>
              <p>By clicking "I Agree &amp; Proceed to Payment", you confirm that you have read and understood all terms and agree to be legally bound by this agreement.</p>
            </div>

            {/* Custom checkbox with visible tick */}
            <label className="af-consent-check">
              <span className={`af-consent-checkbox ${consentChecked ? "af-consent-checkbox--checked" : ""}`}>
                {consentChecked && (
                  <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                    <path d="M1 5l3.5 3.5L11 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
              />
              <span>I have read, understood, and agree to all the terms and conditions of TheVisaCo.</span>
            </label>
          </div>

          <div className="af-consent-actions">
            <button className="pb-cancel-btn" onClick={() => setStep(1)}>Back </button>
            <button
              className={`pg-btn-primary ${!consentChecked ? "pg-btn-primary--disabled" : ""}`}
              onClick={handleConsentNext}
              disabled={!consentChecked}
            >
              I Agree
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── STEP 1 — Application Form ── */
  return (
    <div className="af-root">
      <div className="af-container">
        <div className="af-header">
          <h2 className="af-title">Start Your Passport Application</h2>
          <p className="af-sub">Fill in the details below. Our team will take care of everything from here.</p>
        </div>

        {/* ── Desktop: 2-column grid with applicant navigation ── */}
        <div className="af-desktop-only">
          <div className="af-desktop-grid">
            <div className="af-grid-left">
              <div key={currentApplicant} className={`af-anim-wrap ${slideDir}`}>
                <div className="af-section af-applicant-section">
                  <h3 className="af-section-label">
                    {numApplicants > 1 ? `Applicant ${currentApplicant + 1}` : "Applicant Details"}
                  </h3>
                  <div className="pb-form-grid">
                    <div className="pb-field">
                      <label className="pb-label">First Name * <span className="af-hint">(as per Aadhaar / PAN)</span></label>
                      <input className={`pb-input ${errors[currentApplicant]?.firstName ? "pb-input--error" : ""}`} placeholder="First name" value={applicants[currentApplicant].firstName} onChange={(e) => updateApplicant(currentApplicant, "firstName", e.target.value)} />
                      {errors[currentApplicant]?.firstName && <span className="pb-error">{errors[currentApplicant].firstName}</span>}
                    </div>
                    <div className="pb-field">
                      <label className="pb-label">Last Name</label>
                      <input className="pb-input" placeholder="Surname (optional)" value={applicants[currentApplicant].lastName} onChange={(e) => updateApplicant(currentApplicant, "lastName", e.target.value)} />
                    </div>
                    <div className="pb-field">
                      <label className="pb-label">Phone Number *</label>
                      <div className="pb-phone-row">
                        <select className="pb-input pb-phone-code" value={applicants[currentApplicant].phoneCode} onChange={(e) => updateApplicant(currentApplicant, "phoneCode", e.target.value)}>
                          {COUNTRY_CODES.map(cc => (
                            <option key={cc.code} value={cc.code}>{cc.flag} {cc.code}</option>
                          ))}
                        </select>
                        <input className={`pb-input pb-phone-input ${errors[currentApplicant]?.phone ? "pb-input--error" : ""}`} placeholder="Phone number" value={applicants[currentApplicant].phone} onChange={(e) => updateApplicant(currentApplicant, "phone", e.target.value.replace(/\D/g, ""))} />
                      </div>
                      {errors[currentApplicant]?.phone && <span className="pb-error">{errors[currentApplicant].phone}</span>}
                    </div>
                    <div className="pb-field">
                      <label className="pb-label">Emergency Contact *</label>
                      <div className="pb-phone-row">
                        <select className="pb-input pb-phone-code" value={applicants[currentApplicant].emergencyCode} onChange={(e) => updateApplicant(currentApplicant, "emergencyCode", e.target.value)}>
                          {COUNTRY_CODES.map(cc => (
                            <option key={cc.code} value={cc.code}>{cc.flag} {cc.code}</option>
                          ))}
                        </select>
                        <input className={`pb-input pb-phone-input ${errors[currentApplicant]?.emergencyContact ? "pb-input--error" : ""}`} placeholder="Emergency contact number" value={applicants[currentApplicant].emergencyContact} onChange={(e) => updateApplicant(currentApplicant, "emergencyContact", e.target.value.replace(/\D/g, ""))} />
                      </div>
                      {errors[currentApplicant]?.emergencyContact && <span className="pb-error">{errors[currentApplicant].emergencyContact}</span>}
                    </div>
                    <div className="pb-field">
                      <label className="pb-label">ID Number * <span className="af-hint">(Aadhaar / PAN / Voter ID)</span></label>
                      <input className={`pb-input ${errors[currentApplicant]?.idNumber ? "pb-input--error" : ""}`} placeholder="Enter your ID number" value={applicants[currentApplicant].idNumber} onChange={(e) => updateApplicant(currentApplicant, "idNumber", e.target.value)} />
                      {errors[currentApplicant]?.idNumber && <span className="pb-error">{errors[currentApplicant].idNumber}</span>}
                    </div>
                    <div className="pb-field">
                      <label className="pb-label">Country</label>
                      <input className="pb-input" placeholder="Enter your country" value={applicants[currentApplicant].country} onChange={(e) => updateApplicant(currentApplicant, "country", e.target.value)} />
                    </div>
                    <div className="pb-field">
                      <label className="pb-label">Full Address *</label>
                      <textarea className={`pb-textarea ${errors[currentApplicant]?.address ? "pb-input--error" : ""}`} placeholder="House / Flat no., Street, Area, City, State" rows={3} value={applicants[currentApplicant].address} onChange={(e) => updateApplicant(currentApplicant, "address", e.target.value)} />
                      {errors[currentApplicant]?.address && <span className="pb-error">{errors[currentApplicant].address}</span>}
                    </div>
                    <div className="pb-field">
                      <label className="pb-label">Postal Code *</label>
                      <input className={`pb-input ${errors[currentApplicant]?.postalCode ? "pb-input--error" : ""}`} placeholder="6-digit PIN code" maxLength={6} value={applicants[currentApplicant].postalCode} onChange={(e) => updateApplicant(currentApplicant, "postalCode", e.target.value.replace(/\D/g, ""))} />
                      {errors[currentApplicant]?.postalCode && <span className="pb-error">{errors[currentApplicant].postalCode}</span>}
                    </div>
                  </div>
                  <div className="af-doc-section">
                    <h4 className="af-doc-heading">Upload Documents</h4>
                    <p className="af-doc-sub">Upload whichever documents you have — Aadhaar, Passport, or PAN card. Both front and back required.</p>
                    <div className="af-doc-grid">
                      {[
                        { key: "aadhaar_front",   label: "Aadhaar Card — Front" },
                        { key: "aadhaar_back",    label: "Aadhaar Card — Back" },
                        { key: "passport_front",  label: "Existing Passport — Front" },
                        { key: "passport_back",   label: "Existing Passport — Back" },
                        { key: "pan_front",       label: "PAN Card — Front" },
                        { key: "pan_back",        label: "PAN Card — Back" },
                      ].map(({ key, label }) => (
                        <DocUploader key={key} label={label} doc={applicants[currentApplicant].documents[key]} onChange={(file) => updateApplicantDoc(currentApplicant, key, file)} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {numApplicants > 1 && (
                <div className="af-nav-actions">
                  <button className="pb-cancel-btn af-nav-btn" onClick={handlePrevApplicant} disabled={currentApplicant === 0}>
                    ← 
                  </button>
                  <span className="af-nav-counter">Applicant {currentApplicant + 1} of {numApplicants}</span>
                  <button className="pg-btn-primary af-nav-btn" onClick={handleNextApplicant} disabled={currentApplicant === numApplicants - 1}>
                     →
                  </button>
                </div>
              )}
            </div>

            <div className="af-grid-right">
              <div className="af-section">
                <h3 className="af-section-label">Select Service</h3>
                <div className="af-field af-field--full">
                  <label className="pb-label">Service Type *</label>
                  <select className="pb-input af-select" value={service} onChange={(e) => setService(e.target.value)}>
                    <option value="">— Choose Your Service —</option>
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </div>

                {selectedService && (
                  <div className="af-service-info">
                    <div className="af-service-info-row">
                      <div className="af-info-chip">{selectedService.bookletPages}-page booklet</div>
                      {/* <div className="af-info-chip">Govt. Fee: Rs. {selectedService.govtFee}/person</div>
                      <div className="af-info-chip">Our Fee: Rs. {selectedService.serviceFee}/person</div> */}
                    </div>
                  </div>
                )}

                <div className="af-buddy-toggle">
                  <label className="af-buddy-label">
                    <div className="af-buddy-toggle-info">
                      <span className="af-buddy-title">Add Passport Buddy — Optional</span>
                      <span className="af-buddy-desc">Your Passport Buddy will be there for you</span>
                    </div>
                    <div className="af-buddy-pricing">
                      {/* <span>Rs. 1,999 </span> */}
                    </div>
                    <div className="af-toggle-wrap">
                      <input type="checkbox" checked={passportBuddy} onChange={(e) => setPassportBuddy(e.target.checked)} className="af-toggle-input" />
                      <span className={`af-toggle-switch ${passportBuddy ? "af-toggle-switch--on" : ""}`}></span>
                    </div>
                  </label>
                </div>

                <div className="af-field">
                  <label className="pb-label">Number of Applicants *</label>
                  <div className="af-counter">
                    <button type="button" className="af-counter-btn" onClick={() => handleNumApplicantsChange(numApplicants - 1)}>−</button>
                    <span className="af-counter-val">{numApplicants}</span>
                    <button type="button" className="af-counter-btn" onClick={() => handleNumApplicantsChange(numApplicants + 1)}>+</button>
                  </div>
                  <span style={{ fontSize: "12px", color: "var(--ink-light)", marginTop: "6px", display: "block", fontWeight: 300 }}>Each applicant receives a unique Application ID</span>
                </div>
              </div>

              {selectedService && (
                <div className="af-price-preview">
                  <h3 className="af-section-label">Estimated Price</h3>
                  <div className="af-price-rows">
                    <div className="af-price-row"><span>Service Fee ({numApplicants} applicant{numApplicants > 1 ? "s" : ""} x Rs. {selectedService.serviceFee})</span><strong>Rs. {serviceFeeTotal.toLocaleString("en-IN")}</strong></div>
                    <div className="af-price-row"><span>Govt. Fee ({numApplicants} applicant{numApplicants > 1 ? "s" : ""} x Rs. {selectedService.govtFee})</span><strong>Rs. {govtFeeTotal.toLocaleString("en-IN")}</strong></div>
                    <div className="af-price-row"><span>Convenience Fee</span><strong>{convFee === 0 ? "Free" : `Rs. ${convFee}`}</strong></div>
                    {passportBuddy && <div className="af-price-row"><span>Passport Buddy</span><strong>Rs. {buddyFee.toLocaleString("en-IN")}</strong></div>}
                    <div className="af-price-row"><span>GST (18%)</span><strong>Rs. {gst.toLocaleString("en-IN")}</strong></div>
                    <div className="af-price-row af-price-row--total"><span>Total</span><strong>Rs. {grandTotal.toLocaleString("en-IN")}</strong></div>
                  </div>
                  <div className="af-form-actions af-form-actions--inside-preview">
                    <button className="pg-btn-primary af-submit-btn" onClick={handleFormNext}>
                      Proceed 
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Mobile & Tablet ── */}
        <div className="af-mob-tab-only">
          <div className="af-section">
            <h3 className="af-section-label">Select Service</h3>
            <div className="af-field af-field--full">
              <label className="pb-label">Service Type *</label>
              <select className="pb-input af-select" value={service} onChange={(e) => setService(e.target.value)}>
                <option value="">— Choose Your Service —</option>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>

            {selectedService && (
              <div className="af-service-info">
                <div className="af-service-info-row">
                  <div className="af-info-chip">{selectedService.bookletPages}-page booklet</div>
                  {/* <div className="af-info-chip">Govt. Fee: Rs. {selectedService.govtFee}/person</div>
                  <div className="af-info-chip">Our Fee: Rs. {selectedService.serviceFee}/person</div> */}
                </div>
              </div>
            )}

            <div className="af-buddy-toggle">
              <label className="af-buddy-label">
                <div className="af-buddy-toggle-info">
                  <span className="af-buddy-title">Add Passport Buddy — Optional</span>
                  <span className="af-buddy-desc">Your Passport Buddy will be there for you</span>
                </div>
                <div className="af-buddy-pricing">
                  {/* <span>Rs. 1,999 per application</span> */}
                </div>
                <div className="af-toggle-wrap">
                  <input type="checkbox" checked={passportBuddy} onChange={(e) => setPassportBuddy(e.target.checked)} className="af-toggle-input" />
                  <span className={`af-toggle-switch ${passportBuddy ? "af-toggle-switch--on" : ""}`}></span>
                </div>
              </label>
            </div>

            <div className="af-field">
              <label className="pb-label">Number of Applicants *</label>
              <div className="af-counter">
                <button type="button" className="af-counter-btn" onClick={() => handleNumApplicantsChange(numApplicants - 1)}>−</button>
                <span className="af-counter-val">{numApplicants}</span>
                <button type="button" className="af-counter-btn" onClick={() => handleNumApplicantsChange(numApplicants + 1)}>+</button>
              </div>
              <span style={{ fontSize: "12px", color: "var(--ink-light)", marginTop: "6px", display: "block", fontWeight: 300 }}>Each applicant receives a unique Application ID</span>
            </div>
          </div>

          <div key={currentApplicant} className={`af-anim-wrap ${slideDir}`}>
            <div className="af-section af-applicant-section">
              <h3 className="af-section-label">
                {numApplicants > 1 ? `Applicant ${currentApplicant + 1}` : "Applicant Details"}
              </h3>
              <div className="pb-form-grid">
                <div className="pb-field">
                  <label className="pb-label">First Name * <span className="af-hint">(as per Aadhaar / PAN)</span></label>
                  <input className={`pb-input ${errors[currentApplicant]?.firstName ? "pb-input--error" : ""}`} placeholder="First name" value={applicants[currentApplicant].firstName} onChange={(e) => updateApplicant(currentApplicant, "firstName", e.target.value)} />
                  {errors[currentApplicant]?.firstName && <span className="pb-error">{errors[currentApplicant].firstName}</span>}
                </div>
                <div className="pb-field">
                  <label className="pb-label">Last Name</label>
                  <input className="pb-input" placeholder="Surname (optional)" value={applicants[currentApplicant].lastName} onChange={(e) => updateApplicant(currentApplicant, "lastName", e.target.value)} />
                </div>
                <div className="pb-field">
                  <label className="pb-label">Phone Number *</label>
                  <div className="pb-phone-row">
                    <select className="pb-input pb-phone-code" value={applicants[currentApplicant].phoneCode} onChange={(e) => updateApplicant(currentApplicant, "phoneCode", e.target.value)}>
                      {COUNTRY_CODES.map(cc => (
                        <option key={cc.code} value={cc.code}>{cc.flag} {cc.code}</option>
                      ))}
                    </select>
                    <input className={`pb-input pb-phone-input ${errors[currentApplicant]?.phone ? "pb-input--error" : ""}`} placeholder="Phone number" value={applicants[currentApplicant].phone} onChange={(e) => updateApplicant(currentApplicant, "phone", e.target.value.replace(/\D/g, ""))} />
                  </div>
                  {errors[currentApplicant]?.phone && <span className="pb-error">{errors[currentApplicant].phone}</span>}
                </div>
                <div className="pb-field">
                  <label className="pb-label">Emergency Contact *</label>
                  <div className="pb-phone-row">
                    <select className="pb-input pb-phone-code" value={applicants[currentApplicant].emergencyCode} onChange={(e) => updateApplicant(currentApplicant, "emergencyCode", e.target.value)}>
                      {COUNTRY_CODES.map(cc => (
                        <option key={cc.code} value={cc.code}>{cc.flag} {cc.code}</option>
                      ))}
                    </select>
                    <input className={`pb-input pb-phone-input ${errors[currentApplicant]?.emergencyContact ? "pb-input--error" : ""}`} placeholder="Emergency contact number" value={applicants[currentApplicant].emergencyContact} onChange={(e) => updateApplicant(currentApplicant, "emergencyContact", e.target.value.replace(/\D/g, ""))} />
                  </div>
                  {errors[currentApplicant]?.emergencyContact && <span className="pb-error">{errors[currentApplicant].emergencyContact}</span>}
                </div>
                <div className="pb-field">
                  <label className="pb-label">ID Number * <span className="af-hint">(Aadhaar / PAN / Voter ID)</span></label>
                  <input className={`pb-input ${errors[currentApplicant]?.idNumber ? "pb-input--error" : ""}`} placeholder="Enter your ID number" value={applicants[currentApplicant].idNumber} onChange={(e) => updateApplicant(currentApplicant, "idNumber", e.target.value)} />
                  {errors[currentApplicant]?.idNumber && <span className="pb-error">{errors[currentApplicant].idNumber}</span>}
                </div>
                <div className="pb-field">
                  <label className="pb-label">Country</label>
                  <input className="pb-input" placeholder="Enter your country" value={applicants[currentApplicant].country} onChange={(e) => updateApplicant(currentApplicant, "country", e.target.value)} />
                </div>
                <div className="pb-field">
                  <label className="pb-label">Full Address *</label>
                  <textarea className={`pb-textarea ${errors[currentApplicant]?.address ? "pb-input--error" : ""}`} placeholder="House / Flat no., Street, Area, City, State" rows={3} value={applicants[currentApplicant].address} onChange={(e) => updateApplicant(currentApplicant, "address", e.target.value)} />
                  {errors[currentApplicant]?.address && <span className="pb-error">{errors[currentApplicant].address}</span>}
                </div>
                <div className="pb-field">
                  <label className="pb-label">Postal Code *</label>
                  <input className={`pb-input ${errors[currentApplicant]?.postalCode ? "pb-input--error" : ""}`} placeholder="6-digit PIN code" maxLength={6} value={applicants[currentApplicant].postalCode} onChange={(e) => updateApplicant(currentApplicant, "postalCode", e.target.value.replace(/\D/g, ""))} />
                  {errors[currentApplicant]?.postalCode && <span className="pb-error">{errors[currentApplicant].postalCode}</span>}
                </div>
              </div>
              <div className="af-doc-section">
                <h4 className="af-doc-heading">Upload Documents</h4>
                <p className="af-doc-sub">Upload whichever documents you have — Aadhaar, Passport, or PAN card. Both front and back required.</p>
                <div className="af-doc-grid">
                  {[
                    { key: "aadhaar_front",   label: "Aadhaar Card — Front" },
                    { key: "aadhaar_back",    label: "Aadhaar Card — Back" },
                    { key: "passport_front",  label: "Existing Passport — Front" },
                    { key: "passport_back",   label: "Existing Passport — Back" },
                    { key: "pan_front",       label: "PAN Card — Front" },
                    { key: "pan_back",        label: "PAN Card — Back" },
                  ].map(({ key, label }) => (
                    <DocUploader key={key} label={label} doc={applicants[currentApplicant].documents[key]} onChange={(file) => updateApplicantDoc(currentApplicant, key, file)} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {numApplicants > 1 && (
            <div className="af-nav-actions">
              <button className="pb-cancel-btn af-nav-btn" onClick={handlePrevApplicant} disabled={currentApplicant === 0}>
                ← 
              </button>
              <span className="af-nav-counter">Applicant {currentApplicant + 1} of {numApplicants}</span>
              <button className="pg-btn-primary af-nav-btn" onClick={handleNextApplicant} disabled={currentApplicant === numApplicants - 1}>
                 →
              </button>
            </div>
          )}

          {selectedService && (
            <div className="af-price-preview">
              <h3 className="af-section-label">Estimated Price</h3>
              <div className="af-price-rows">
                <div className="af-price-row"><span>Service Fee ({numApplicants} applicant{numApplicants > 1 ? "s" : ""} x Rs. {selectedService.serviceFee})</span><strong>Rs. {serviceFeeTotal.toLocaleString("en-IN")}</strong></div>
                <div className="af-price-row"><span>Govt. Fee ({numApplicants} applicant{numApplicants > 1 ? "s" : ""} x Rs. {selectedService.govtFee})</span><strong>Rs. {govtFeeTotal.toLocaleString("en-IN")}</strong></div>
                <div className="af-price-row"><span>Convenience Fee</span><strong>{convFee === 0 ? "Free" : `Rs. ${convFee}`}</strong></div>
                {passportBuddy && <div className="af-price-row"><span>Passport Buddy</span><strong>Rs. {buddyFee.toLocaleString("en-IN")}</strong></div>}
                <div className="af-price-row"><span>GST (18%)</span><strong>Rs. {gst.toLocaleString("en-IN")}</strong></div>
                <div className="af-price-row af-price-row--total"><span> Total</span><strong>Rs. {grandTotal.toLocaleString("en-IN")}</strong></div>
              </div>
            </div>
          )}

          <div className="af-form-actions">
            <button className="pg-btn-primary af-submit-btn" onClick={handleFormNext}>
              Proceed 
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocUploader({ label, doc, onChange }) {
  const inputRef = useRef(null);
  return (
    <div className={`af-doc-uploader ${doc ? "af-doc-uploader--done" : ""}`}>
      <div className="af-doc-label">{label}</div>
      {doc ? (
        <div className="af-doc-preview">
          <div className="af-doc-info">
            <span className="af-doc-name">{doc.name}</span>
            <div className="af-doc-actions">
              <a href={doc.url} download={doc.name} className="af-doc-btn">Download</a>
              <button className="af-doc-btn af-doc-btn--remove" onClick={() => onChange(null)}>Remove</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="af-doc-empty" onClick={() => inputRef.current?.click()}>
          <span>Upload {label}</span>
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => onChange(e.target.files[0])} />
    </div>
  );
}

function SuccessScreen({ appData, onReset }) {
   const downloadReceipt = async () => {
     try {
       const { jsPDF } = await import("jspdf");
       const html2canvas = (await import("html2canvas")).default;
       const toBase64 = async (url) => {
         const resp = await fetch(url);
         const blob = await resp.blob();
         return new Promise((r) => {
           const reader = new FileReader();
           reader.onloadend = () => r(reader.result);
           reader.readAsDataURL(blob);
         });
       };
       let logoSrc = null;
       try { logoSrc = await toBase64(receiptLogo); } catch { logoSrc = null; }

        const primaryApplicant = appData.applicants[0] || {};
        const isPayByReward = appData.paymentMode === "Pay by Reward";
        const discountAmt = isPayByReward ? appData.rewardUsed : (appData.rewardDiscount || 0);
        const item = {
          label: "Passport Application",
          countryName: appData.service.label,
          price: appData.serviceFeeTotal,
          data: `${appData.numApplicants} Applicant(s)`,
          validity: 1,
          type: "service",
        };

        const html = generateReceiptHTML({
          item, quantity: 1, payMethod: isPayByReward ? "reward" : (appData.paymentMode.id || "upi"),
          billingDetails: {
            firstName: primaryApplicant.firstName || "",
            lastName: primaryApplicant.lastName || "",
            email: "",
            country: "IN",
            phone: primaryApplicant.phone || "",
            phoneCode: primaryApplicant.phoneCode || "+91",
            whatsapp: primaryApplicant.phone || "",
            address: primaryApplicant.address || "",
            city: primaryApplicant.postalCode || "",
            pincode: primaryApplicant.postalCode || "",
          },
          applicants: appData.applicants.map(a => ({
            firstName: a.firstName,
            lastName: a.lastName,
            phone: a.phone,
            phoneCode: a.phoneCode || "+91",
            aadhaarFront: a.documents?.aadhaar_front,
            aadhaarBack: a.documents?.aadhaar_back,
            passportAppFront: a.documents?.passport_front,
            passportAppBack: a.documents?.passport_back,
            panFront: a.documents?.pan_front,
            panBack: a.documents?.pan_back,
          })),
           orderId: appData.applicationId,
           currency: "INR",
           appliedDiscount: discountAmt,
           logoSrc, pdf: true, inr: true,
           upiId: appData.upiId || "",
           bank: isPayByReward ? "Pay by Reward" : (appData.paymentMode.label || ""),
           card: { number: "" },
           simType: "",
           overrides: {
              govtFee: appData.govtFeeTotal,
              paymentMethodLabel: isPayByReward ? "Pay by Reward" : appData.paymentMode.label,
              subtotal: appData.serviceFeeTotal + appData.buddyFee,
              convFee: appData.convFee,
              tax: appData.gst,
              total: isPayByReward ? 0 : appData.grandTotal,
              discountLabel: isPayByReward ? "Paid by Reward" : "Discount Applied",
            },
        });

      const container = document.createElement("div");
      container.innerHTML = html;
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "0";
      container.style.width = "210mm";
      container.style.background = "#f5f9ff";
      container.style.overflow = "visible";
      document.body.appendChild(container);
      const el = container.querySelector(".Receipt-card") || container.firstElementChild;
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: "#f5f9ff", height: el.scrollHeight, windowHeight: el.scrollHeight });
      document.body.removeChild(container);

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`TheVisaCo_Receipt_${appData.applicationId}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    }
  };

  return (
    <div className="af-root">
      <div className="af-container">
        <div className="af-success">
          {/* <div className="af-success-icon">&#10003;</div> */}
          <h2 className="af-success-title">Application Submitted</h2>
          <p className="af-success-msg">
            Your application has been received. Our team will contact you within 24 hours to begin processing. Keep your Track ID — you can use it to monitor your application status at any time.
          </p>
          <div className="af-id-cards">
            <div className="af-id-card">
              <span className="af-id-label">Application ID</span>
              <span className="af-id-value">{appData.applicationId}</span>
            </div>
            <div className="af-id-card af-id-card--track">
              <span className="af-id-label">Track ID</span>
              <span className="af-id-value">{appData.trackId}</span>
            </div>
          </div>
              <div className="af-receipt-summary">
               <div className="af-receipt-row"><span>Service</span><strong>{appData.service.label}</strong></div>
               <div className="af-receipt-row"><span>Applicants</span><strong>{appData.numApplicants}</strong></div>
               <div className="af-receipt-row"><span>Passport Buddy</span><strong>{appData.passportBuddy ? "Yes — Included" : "Not Availed"}</strong></div>
               <div className="af-summary-divider" style={{ margin: "8px 0", borderBottom: "1px dashed var(--border-light)" }} />
               <div className="af-receipt-row"><span>Date Booked</span><strong>{appData.submittedAt}</strong></div>
               <div className="af-receipt-row">
                 <span>Payment Method</span>
                 <strong>{appData.paymentMode === "Pay by Reward" ? "Pay by Reward" : appData.paymentMode.label}</strong>
               </div>
               <div className="af-receipt-row">
                 <span>Government Fee <span style={{ fontSize: "10px", color: "var(--ink-light)" }}></span></span>
                 <strong>Rs. {appData.govtFeeTotal.toLocaleString("en-IN")}</strong>
               </div>
               <div className="af-receipt-row"><span>Service Fee</span><strong>Rs. {appData.serviceFeeTotal.toLocaleString("en-IN")}</strong></div>
               <div className="af-receipt-row"><span>GST (18%)</span><strong>Rs. {appData.gst.toLocaleString("en-IN")}</strong></div>
               <div className="af-receipt-row"><span>Convenience Fee</span><strong>{appData.convFee === 0 ? "Free" : `Rs. ${appData.convFee}`}</strong></div>
               {appData.paymentMode !== "Pay by Reward" && appData.rewardDiscount > 0 && (
                 <div className="af-receipt-row">
                   <span>Discount Applied</span>
                   <strong style={{ color: "#16a34a" }}>− Rs. {appData.rewardDiscount.toLocaleString("en-IN")}</strong>
                 </div>
               )}
               {appData.buddyFee > 0 && (
                 <div className="af-receipt-row"><span>Passport Buddy Fee</span><strong>Rs. {appData.buddyFee.toLocaleString("en-IN")}</strong></div>
               )}
               {appData.paymentMode === "Pay by Reward" ? (
                 <>
                   {appData.rewardUsed > 0 && (
                     <div className="af-receipt-row">
                       <span>Paid by Reward</span>
                       <strong style={{ color: "#dc2626" }}>− Rs. {appData.rewardUsed.toLocaleString("en-IN")}</strong>
                     </div>
                   )}
                   <div className="af-summary-divider" style={{ margin: "8px 0", borderBottom: "1px dashed var(--border-light)" }} />
                   <div className="af-receipt-row af-receipt-row--total">
                     <span>Total </span>
                     <strong>Rs. 0</strong>
                   </div>
                 </>
               ) : (
                 <>
                   <div className="af-summary-divider" style={{ margin: "8px 0", borderBottom: "1px dashed var(--border-light)" }} />
                   <div className="af-receipt-row af-receipt-row--total">
                     <span>Total </span>
                      <strong>Rs. {appData.grandTotal.toLocaleString("en-IN")}</strong>
                   </div>
                 </>
               )}
            </div>

          {/* Success actions — full width stack on mobile */}
          <div className="af-success-actions">
            <button className="pg-btn-primary" onClick={downloadReceipt}>Download Receipt</button>
            <button className="pb-cancel-btn" onClick={onReset}>Submit Another Application</button>
          </div>

          {/* <p className="af-track-note">
            Use Track ID <strong>{appData.trackId}</strong> in the Track Application section to check your appointment and status updates.
          </p> */}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Passportmodal.js (original) - Passport Buddy Modal
// =============================================================================










function LoginGate({ open, onClose, onSuccess }) {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  function handleLoginSuccess(user) {
    onSuccess(user);
    onClose();
  }

  return (
    <div
      className="lg-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="lg-inner">
        <button className="lg-close" onClick={onClose}>✕</button>
        <Login onLoginSuccess={handleLoginSuccess} />
      </div>
    </div>
  );
}

function deductWalletDiscountPB(email, discountAmount, description, totalAmount) {
  if (!email || discountAmount <= 0) return { success: false };
  const wallet = getWallet(email);
  const totalBalance = (wallet.redeemable?.balance || 0) + (wallet.nonRedeemable?.balance || 0);
  if (totalBalance < discountAmount) return { success: false, wallet };

  let remaining = discountAmount;
  const redeemableUsed = Math.min(wallet.redeemable?.balance || 0, remaining);
  if (redeemableUsed > 0) {
    wallet.redeemable.balance -= redeemableUsed;
    remaining -= redeemableUsed;
    wallet.redeemable.transactions = wallet.redeemable.transactions || [];
    wallet.redeemable.transactions.unshift({
      id: `discount_${Date.now()}_r`,
      label: description || "Reward Discount Applied",
      amount: -redeemableUsed,
      type: "debit",
      date: new Date().toISOString(),
      note: "Applied as discount on Passport Buddy service fee",
      totalPrice: totalAmount || 0,
    });
  }

  if (remaining > 0) {
    const nonRedeemableUsed = Math.min(wallet.nonRedeemable?.balance || 0, remaining);
    if (nonRedeemableUsed > 0) {
      wallet.nonRedeemable.balance -= nonRedeemableUsed;
      wallet.nonRedeemable.transactions = wallet.nonRedeemable.transactions || [];
      wallet.nonRedeemable.transactions.unshift({
        id: `discount_${Date.now()}_nr`,
        label: description || "Reward Discount Applied",
        amount: -nonRedeemableUsed,
        type: "debit",
        date: new Date().toISOString(),
        note: "Applied as discount on Passport Buddy service fee",
        totalPrice: totalAmount || 0,
      });
    }
  }

  saveWallet(email, wallet);
  return { success: true, wallet };
}

export const PASSPORT_GUIDE_SECTIONS = [
  { id: "new",     label: "New Passport"        },
  { id: "renewal", label: "Renewal Process"     },
  { id: "reissue", label: "Reissue Process"     },
  { id: "tatkaal", label: "Tatkaal vs Normal"   },
];

const EMPTY_BUDDY_APPLICANT = () => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  phoneCode: "+91",
  passportNumber: "",
  address: "",
  city: "",
  state: "",
  postalCode: "",
  country: "India",
});



// T+1 (immediate) = 2499 (premium for same-day booking), T+2 onwards = 1499
const BUDDY_IMMEDIATE_FEE = 2499;
const BUDDY_STANDARD_FEE  = 1499;

const TIME_SLOTS = [
  "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM",
  "02:00 PM", "02:30 PM", "03:00 PM", "03:30 PM",
  "04:00 PM", "04:30 PM",
];

function getTomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
}

function getDayAfterTomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return d.toISOString().split("T")[0];
}

function isImmediateDate(dateStr) {
  return dateStr === getTomorrowStr();
}

function generateBuddyId() {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `TVC-PB-${ts}-${rand}`;
}

export default function PassportBuddyModal({ onClose }) {
  const [step, setStep]           = useState(1);
  const [applicant, setApplicant] = useState(EMPTY_BUDDY_APPLICANT());
  const [apptDate, setApptDate]   = useState("");
  const [apptTime, setApptTime]   = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [payMethod, setPayMethod]             = useState("debit");
  const [paymentDetails, setPaymentDetails]   = useState(PAYMENT_DETAILS_DEFAULT);
  const setPayF = (e) => setPaymentDetails(d => ({ ...d, [e.target.name]: e.target.value }));
  const [errors, setErrors]         = useState({});
  const [buddyId]                   = useState(generateBuddyId);
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("");
  const [centerAddress, setCenterAddress] = useState("");
  const [centerCity, setCenterCity] = useState("");
  const [centerState, setCenterState] = useState("");
  const [centerPostalCode, setCenterPostalCode] = useState("");
  const [showLoginGate, setShowLoginGate] = useState(false);
  const [rewardDiscountAmount, setRewardDiscountAmount] = useState(0);

  const updateField = (field, value) => setApplicant((p) => ({ ...p, [field]: value }));

  const immediate      = apptDate ? isImmediateDate(apptDate) : false;
  const buddyServiceFee = apptDate ? (immediate ? BUDDY_IMMEDIATE_FEE : BUDDY_STANDARD_FEE) : BUDDY_IMMEDIATE_FEE;

  const { user: session } = useAuth();
  const isLoggedIn = !!session?.email;
  const walletBalances = isLoggedIn ? getCurrentWalletBalances(session?.email) : null;
  const maxRewardDiscount = Math.round(buddyServiceFee * REWARD_DISCOUNT_MAX_RATE);

  const payMethodObj = PAYMENT_METHODS_LIST.find(m => m.id === payMethod) || PAYMENT_METHODS_LIST[0];
  const subtotal   = buddyServiceFee - rewardDiscountAmount;
  const gst        = Math.round((buddyServiceFee) * 0.18);
  const convFee    = payMethodObj.fee;
  const total = subtotal + gst + convFee;

  const validateStep1 = () => {
    const e = {};
    if (!applicant.firstName.trim()) e.firstName = "First name is required.";
    if (!applicant.phone.trim() || applicant.phone.trim().length < 4) e.phone = "Enter a valid phone number.";
    if (!applicant.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applicant.email.trim())) e.email = "Enter a valid email address.";
    if (applicant.passportNumber.trim() && !/^[A-Za-z0-9]{6,16}$/.test(applicant.passportNumber.trim()))
      e.passportNumber = "Enter a valid ID (Passport / Aadhaar / PAN).";
    if (!applicant.address.trim()) e.address = "Address is required.";
    if (!applicant.city.trim()) e.city = "City is required.";
    if (!applicant.state.trim()) e.state = "State is required.";
    if (!/^\d{6}$/.test(applicant.postalCode.trim())) e.postalCode = "Enter a valid 6-digit postal code.";
    if (!centerAddress.trim()) e.centerAddress = "Center address is required.";
    if (!centerCity.trim()) e.centerCity = "Center city is required.";
    if (!centerState.trim()) e.centerState = "Center state is required.";
    if (!/^\d{6}$/.test(centerPostalCode.trim())) e.centerPostalCode = "Enter a valid 6-digit postal code.";
    if (!apptDate) e.apptDate = "Please select your appointment date.";
    if (!apptTime) e.apptTime = "Please select a time slot.";
    return e;
  };

  const validatePayment = () => {
    const err = validatePaymentDetails(payMethod, paymentDetails);
    if (err) return { _general: err };
    return {};
  };

  const handleStep1Next = () => {
    const errs = validateStep1();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setStep(2);
  };

  const handleConsentNext = () => {
    if (!consentChecked) return; // button is disabled anyway
    setStep(3);
  };

  const handlePaymentSubmit = () => {
    if (payMethod === "reward") {
      if (!isLoggedIn || !session?.email) {
        alert("Please sign in to use Pay by Reward.");
        return;
      }
      if (walletBalances && total > walletBalances.total) {
        alert(`Insufficient wallet balance. You need ₹${total.toLocaleString("en-IN")} but have ₹${walletBalances.total.toLocaleString("en-IN")}.`);
        return;
      }
      const result = spendRewardMoney(session.email, total, "Passport Buddy — Pay by Reward");
      if (!result.success) {
        alert("Failed to deduct reward balance. Please try again.");
        return;
      }
    } else {
      if (rewardDiscountAmount > 0) {
        if (!isLoggedIn) {
          alert("Please sign in to use reward discounts.");
          return;
        }
        if (rewardDiscountAmount > maxRewardDiscount) {
          alert(`Discount cannot exceed ${Math.round(REWARD_DISCOUNT_MAX_RATE * 100)}% of the service fee (₹${maxRewardDiscount.toLocaleString("en-IN")}).`);
          return;
        }
        if (walletBalances && rewardDiscountAmount > walletBalances.total) {
          alert(`Insufficient wallet balance. You have ₹${walletBalances.total.toLocaleString("en-IN")} but trying to use ₹${rewardDiscountAmount.toLocaleString("en-IN")}.`);
          return;
        }
      }

      const errs = validatePayment();
      if (Object.keys(errs).length > 0) { setErrors(errs); return; }

      if (rewardDiscountAmount > 0 && session?.email) {
        deductWalletDiscountPB(session.email, rewardDiscountAmount, "Passport Buddy Discount", total + rewardDiscountAmount);
      }
    }

    setErrors({});
    const now = new Date();
    setBookingDate(now.toLocaleDateString("en-IN"));
    setBookingTime(now.toLocaleTimeString("en-IN"));
    savePassportBooking({
      id: buddyId,
      type: "passport-buddy",
      paymentMethod: payMethod === "reward" ? "Pay by Reward" : payMethodObj?.label,
      totalAmount: total,
      rewardDeduction: payMethod === "reward" ? total : (rewardDiscountAmount || 0),
      bookedAt: now.toISOString(),
    }, session?.email);
    setStep(4);
  };

  const handleMethodSelect = (id) => {
    setPayMethod(id);
    setPaymentDetails(PAYMENT_DETAILS_DEFAULT);
  };

   const downloadReceipt = async () => {
     try {
       const { jsPDF } = await import("jspdf");
       const html2canvas = (await import("html2canvas")).default;
       const toBase64 = async (url) => {
         const resp = await fetch(url);
         const blob = await resp.blob();
         return new Promise((r) => {
           const reader = new FileReader();
           reader.onloadend = () => r(reader.result);
           reader.readAsDataURL(blob);
         });
       };
       let logoSrc = null;
       try { logoSrc = await toBase64(receiptLogo); } catch { logoSrc = null; }

       const isPayByReward = payMethod === "reward";
       const methodMap = { upi: "upi", netbanking: "netbanking", debit: "debit", credit: "credit" };
       const payMethodVal = isPayByReward ? "reward" : (methodMap[payMethod] || "upi");

       const item = {
         label: "Passport Buddy",
         countryName: `Appointment: ${apptDate} ${apptTime}`,
         price: buddyServiceFee,
         data: immediate ? "Immediate (T+1)" : "Standard",
         validity: 1,
         type: "service",
       };

       const discountAmt = isPayByReward ? total : (rewardDiscountAmount || 0);
       const html = generateReceiptHTML({
         item, quantity: 1, payMethod: payMethodVal,
         billingDetails: {
           firstName: applicant.firstName,
           lastName: applicant.lastName,
           email: applicant.email,
           country: applicant.country,
           phone: applicant.phone,
           whatsapp: applicant.phone,
           phoneCode: applicant.phoneCode,
           address: applicant.address,
           city: applicant.city,
           state: applicant.state,
           pincode: applicant.postalCode,
         },
         orderId: buddyId,
         currency: "INR",
         appliedDiscount: discountAmt,
         logoSrc, pdf: true, inr: true,
         upiId: isPayByReward ? "" : (paymentDetails.upiId || ""),
         bank: isPayByReward ? "Pay by Reward" : (paymentDetails.bankName || ""),
         card: { number: !isPayByReward && (payMethod === "debit" || payMethod === "credit") ? (paymentDetails.cardNum || "") : "" },
         simType: "",
          overrides: {
            subtotal: buddyServiceFee,
            tax: gst,
            convFee: convFee,
            total: isPayByReward ? 0 : total,
            discountLabel: isPayByReward ? "Pay by Reward" : "Discount",
            subtotalLabel: "Passport Buddy Fee",
          },
       });

      const container = document.createElement("div");
      container.innerHTML = html;
      container.style.position = "fixed";
      container.style.left = "-9999px";
      container.style.top = "0";
      container.style.width = "210mm";
      container.style.background = "#f5f9ff";
      document.body.appendChild(container);
      const el = container.querySelector(".Receipt-card") || container.firstElementChild;
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: "#f5f9ff" });
      document.body.removeChild(container);

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`TheVisaCo-Receipt-${buddyId}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    }
  };

  const formatCard   = (v) => v.replace(/\D/g, "").replace(/(.{4})/g, "$1 ").trim().slice(0, 19);
  const formatExpiry = (v) => { const d = v.replace(/\D/g, "").slice(0, 4); return d.length > 2 ? d.slice(0, 2) + "/" + d.slice(2) : d; };

  const STEP_LABELS = ["Your Details & Appointment", "Consent & Agreement", "Payment"];

  return (
    <div className="pb-overlay" onClick={onClose}>
      <div className="pb-modal pb-modal--wide" onClick={(e) => e.stopPropagation()}>

        {step < 4 && (
          <div className="pb-modal-header">
            <div>
              <h2 className="pb-modal-title">Passport Buddy</h2>
              <p className="pb-modal-sub">Step {step} of 3 — {STEP_LABELS[step - 1]}</p>
            </div>
            <button className="pb-close" onClick={onClose}>&times;</button>
          </div>
        )}

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <>
            <div className="pb-modal-body">
              <div className="pb-section-grid">
              <div className="pb-applicant-block">
                <div className="pb-applicant-header">
                  <h3 className="pb-applicant-title">Your Details</h3>
                </div>
                <div className="pb-form-grid">
                  <div className="pb-field">
                    <label className="pb-label">First Name *</label>
                    <input
                      className={`pb-input ${errors.firstName ? "pb-input--error" : ""}`}
                      placeholder="As per Aadhaar / PAN"
                      value={applicant.firstName}
                      onChange={(e) => updateField("firstName", e.target.value)}
                    />
                    {errors.firstName && <span className="pb-error">{errors.firstName}</span>}
                  </div>
                  <div className="pb-field">
                    <label className="pb-label">Last Name</label>
                    <input
                      className="pb-input"
                      placeholder="Surname (optional)"
                      value={applicant.lastName}
                      onChange={(e) => updateField("lastName", e.target.value)}
                    />
                  </div>
                  <div className="pb-field">
                    <label className="pb-label">Phone Number *</label>
                    <div className="pb-phone-row">
                      <select className="pb-input pb-phone-code" value={applicant.phoneCode} onChange={(e) => updateField("phoneCode", e.target.value)}>
                        {COUNTRY_CODES.map(cc => (
                          <option key={cc.code} value={cc.code}>{cc.flag} {cc.code}</option>
                        ))}
                      </select>
                      <input
                        className={`pb-input pb-phone-input ${errors.phone ? "pb-input--error" : ""}`}
                        placeholder="Number"
                        value={applicant.phone}
                        onChange={(e) => updateField("phone", e.target.value.replace(/\D/g, ""))}
                      />
                    </div>
                    {errors.phone && <span className="pb-error">{errors.phone}</span>}
                  </div>
                  <div className="pb-field">
                    <label className="pb-label">Email ID *</label>
                    <input
                      className={`pb-input ${errors.email ? "pb-input--error" : ""}`}
                      placeholder="your@email.com"
                      value={applicant.email}
                      onChange={(e) => updateField("email", e.target.value)}
                    />
                    {errors.email && <span className="pb-error">{errors.email}</span>}
                  </div>
                  <div className="pb-field">
                    <label className="pb-label">Government ID Proof</label>
                    <input
                      className={`pb-input ${errors.passportNumber ? "pb-input--error" : ""}`}
                      placeholder="e.g. Aadhaar / PAN"
                      maxLength={8}
                      value={applicant.passportNumber}
                      onChange={(e) => updateField("passportNumber", e.target.value.toUpperCase())}
                    />
                    {errors.passportNumber && <span className="pb-error">{errors.passportNumber}</span>}
                  </div>
                  <div className="pb-field">
                    <label className="pb-label">Address *</label>
                    <input
                      className={`pb-input ${errors.address ? "pb-input--error" : ""}`}
                      placeholder="House / Flat no., Street, Area"
                      value={applicant.address}
                      onChange={(e) => updateField("address", e.target.value)}
                    />
                    {errors.address && <span className="pb-error">{errors.address}</span>}
                  </div>
                  <div className="pb-field">
                    <label className="pb-label">City *</label>
                    <input
                      className={`pb-input ${errors.city ? "pb-input--error" : ""}`}
                      placeholder="Your city"
                      value={applicant.city}
                      onChange={(e) => updateField("city", e.target.value)}
                    />
                    {errors.city && <span className="pb-error">{errors.city}</span>}
                  </div>
                  <div className="pb-field">
                    <label className="pb-label">State *</label>
                    <input
                      className={`pb-input ${errors.state ? "pb-input--error" : ""}`}
                      placeholder="Your state"
                      value={applicant.state}
                      onChange={(e) => updateField("state", e.target.value)}
                    />
                    {errors.state && <span className="pb-error">{errors.state}</span>}
                  </div>
                  <div className="pb-field">
                    <label className="pb-label">Postal Code *</label>
                    <input
                      className={`pb-input ${errors.postalCode ? "pb-input--error" : ""}`}
                      placeholder="6-digit PIN code"
                      maxLength={6}
                      value={applicant.postalCode}
                      onChange={(e) => updateField("postalCode", e.target.value.replace(/\D/g, ""))}
                    />
                    {errors.postalCode && <span className="pb-error">{errors.postalCode}</span>}
                  </div>
                  <div className="pb-field">
                    <label className="pb-label">Country *</label>
                    <input
                      className={`pb-input ${errors.country ? "pb-input--error" : ""}`}
                      placeholder="Your country"
                      value={applicant.country}
                      onChange={(e) => updateField("country", e.target.value)}
                    />
                    {errors.country && <span className="pb-error">{errors.country}</span>}
                  </div>
                </div>
              </div>

              {/* Appointment block */}
              <div className="pb-applicant-block">
                <div className="pb-applicant-header">
                  <h3 className="pb-applicant-title">Passport Center Appointment</h3>
                </div>

                {/* Pricing note */}
                <div className="pb-buddy-pricing-note">
                  <div className="pb-buddy-pricing-row pb-buddy-pricing-row--highlight">
                    <span>Tomorrow (Immediate Booking — Premium)</span>
                    <strong>Rs. {BUDDY_IMMEDIATE_FEE.toLocaleString("en-IN")}</strong>
                  </div>
                  <div className="pb-buddy-pricing-row">
                    <span>Day after tomorrow &amp; later (Standard)</span>
                    <strong>Rs. {BUDDY_STANDARD_FEE.toLocaleString("en-IN")}</strong>
                  </div>
                </div>

                <div className="pb-form-grid" style={{ marginTop: "12px" }}>
                  <div className="pb-field">
                    <label className="pb-label">Center Address *</label>
                    <input
                      className={`pb-input ${errors.centerAddress ? "pb-input--error" : ""}`}
                      placeholder="Passport center address"
                      value={centerAddress}
                      onChange={(e) => setCenterAddress(e.target.value)}
                    />
                    {errors.centerAddress && <span className="pb-error">{errors.centerAddress}</span>}
                  </div>
                  <div className="pb-field">
                    <label className="pb-label">City *</label>
                    <input
                      className={`pb-input ${errors.centerCity ? "pb-input--error" : ""}`}
                      placeholder="Center city"
                      value={centerCity}
                      onChange={(e) => setCenterCity(e.target.value)}
                    />
                    {errors.centerCity && <span className="pb-error">{errors.centerCity}</span>}
                  </div>
                  <div className="pb-field">
                    <label className="pb-label">State *</label>
                    <input
                      className={`pb-input ${errors.centerState ? "pb-input--error" : ""}`}
                      placeholder="Center state"
                      value={centerState}
                      onChange={(e) => setCenterState(e.target.value)}
                    />
                    {errors.centerState && <span className="pb-error">{errors.centerState}</span>}
                  </div>
                  <div className="pb-field">
                    <label className="pb-label">Postal Code *</label>
                    <input
                      className={`pb-input ${errors.centerPostalCode ? "pb-input--error" : ""}`}
                      placeholder="6-digit PIN code"
                      maxLength={6}
                      value={centerPostalCode}
                      onChange={(e) => setCenterPostalCode(e.target.value.replace(/\D/g, ""))}
                    />
                    {errors.centerPostalCode && <span className="pb-error">{errors.centerPostalCode}</span>}
                  </div>
                </div>

                <div className="pb-form-grid" style={{ marginTop: "16px" }}>
                  <div className="pb-field pb-appt-field">
                    <label className="pb-label">Appointment Date *</label>
                    <input
                      type="date"
                      className={`pb-input pb-date-input ${errors.apptDate ? "pb-input--error" : ""}`}
                      min={getTomorrowStr()}
                      value={apptDate}
                      onChange={(e) => setApptDate(e.target.value)}
                    />
                    {errors.apptDate && <span className="pb-error">{errors.apptDate}</span>}
                    {apptDate && (
                      <span className="pb-date-tag">
                        {isImmediateDate(apptDate)
                          ? "⚡ Immediate Booking — Rs. " + BUDDY_IMMEDIATE_FEE.toLocaleString("en-IN") + " (Premium)"
                          : "✓ Standard Booking — Rs. " + BUDDY_STANDARD_FEE.toLocaleString("en-IN")}
                      </span>
                    )}
                  </div>
                  <div className="pb-field pb-appt-field">
                    <label className="pb-label">Appointment Time Slot *</label>

                    <select
                      className={`pb-input af-select ${errors.apptTime ? "pb-input--error" : ""}`}
                      value={apptTime}
                      onChange={(e) => setApptTime(e.target.value)}
                    >
                      <option value="">— Select Time Slot —</option>
                      {TIME_SLOTS.map((t) => (<option key={t} value={t}>{t}</option>))}
                    </select>
                    {errors.apptTime && <span className="pb-error">{errors.apptTime}</span>}
                  </div>
                </div>
                {/* <p className="pb-appt-note">
                  Our Passport Buddy agent will be confirmed and will contact you 24 hours before your appointment.
                </p> */}
              </div>
            </div>

              {/* Price preview */}
              <div className="af-price-preview">
                <h3 className="af-section-label">Service Fee Estimate</h3>
                <div className="af-price-rows">
                  <div className="af-price-row">
                    <span>
                      Passport Buddy — 1 Person
                      {apptDate && (
                        <span className="pb-fee-type"> ({immediate ? "Immediate / Premium" : "Standard"})</span>
                      )}
                    </span>
                    <strong>Rs. {buddyServiceFee.toLocaleString("en-IN")}</strong>
                  </div>
                  <div className="af-price-row"><span>GST (18%)</span><strong>Rs. {gst.toLocaleString("en-IN")}</strong></div>
                  <div className="af-price-row af-price-row--total">
                    <span>Total</span>
                    <strong>Rs. {(subtotal + gst).toLocaleString("en-IN")}</strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer — inside modal, always visible */}
            <div className="pb-modal-footer">
              <p className="pb-footer-note">Convenience charge shown at checkout.</p>
              <div className="pb-footer-actions">
                <button className="pb-cancel-btn" onClick={onClose}>Cancel</button>
                <button className="pb-submit-btn" onClick={handleStep1Next}>Proceed </button>
              </div>
            </div>
          </>
        )}

        {/* ── STEP 2 — Consent ── */}
        {step === 2 && (
          <>
            <div className="pb-modal-body">
              <div className="af-consent-box">
                <div className="af-consent-scroll">
                  <h4>Consent &amp; Agreement — Real-Time Passport Assistance Service</h4>
                  <p>By selecting the checkbox and clicking "I Agree", you acknowledge and agree to the following terms and conditions:</p>

                  <h5>1. User Consent for Information Collection</h5>
                  <p>You agree to provide accurate, complete, and up-to-date personal information including but not limited to: full name, date of birth, and contact details; address and identity details; passport-related information; supporting documents (uploaded digitally). You authorise us to use this information solely for the purpose of processing your passport assistance booking.</p>

                  <h5>2. Document Upload &amp; Verification</h5>
                  <p>By proceeding, you confirm that: all documents are genuine and belong to you; you have the legal right to share these documents; any incorrect or fraudulent document submission is solely your responsibility. We are not liable for rejection due to incorrect or incomplete documentation.</p>

                  <h5>3. Service Scope</h5>
                  <p>Our services include: assistance with passport renewal and Tatkal applications; appointment checking and booking at passport centers; application form filling and review; general guidance throughout the process. We act as a facilitation service provider and are not affiliated with any government authority.</p>

                  <h5>4. Appointment Booking</h5>
                  <p>Appointment availability depends on official passport systems and cannot be guaranteed. We will make best efforts to secure the earliest available slot. Any delays or rescheduling by authorities are beyond our control.</p>

                  <h5>5. Payment Terms</h5>
                  <p>All service fees must be paid in advance. Fees include service charges. Payments once made are non-refundable under any circumstances.</p>

                  <h5>6. No Refund Policy</h5>
                  <p>No refunds will be provided for: application rejection; delays from government authorities; incorrect information provided by the user; change of mind after payment.</p>

                  <h5>7. Liability Limitation</h5>
                  <p>We are not responsible for: government decisions, delays, or rejections; errors due to incorrect user-provided information; technical issues on official portals. Our role is limited to assistance and facilitation.</p>

                  <h5>8. On-Ground Assistance (Passport Buddy)</h5>
                  <p>Where applicable, we provide a representative/agent to assist you during your visit to the passport center. This assistance is limited to guidance and does not influence official decisions. Our agent will accompany you through queues, counters, and document verification but has no authority over government officers' decisions.</p>

                  <h5>9. User Responsibility</h5>
                  <p>You agree to: attend appointments on time; carry original documents as required; follow instructions provided by authorities. Failure to comply may result in application rejection.</p>

                  <h5>10. Privacy &amp; Data Protection</h5>
                  <p>Your data will be handled securely and used only for service purposes. We do not sell or share your personal information with third parties except where required for processing your application.</p>

                  <h5>11. Agreement Acceptance</h5>
                  <p>By clicking "I Agree", you confirm that you have read and understood all terms and agree to be legally bound by this agreement.</p>

                  <h5>Appointment Confirmation</h5>
                  <p>Your requested appointment date of <strong>{apptDate}</strong> at <strong>{apptTime}</strong> is subject to agent availability. Our team will confirm within 24 hours.</p>

                  <h5>Declaration</h5>
                  <p>I have read and understood all the terms and conditions mentioned above. I voluntarily consent to use this service.</p>
                </div>

                {/* Custom checkbox with visible tick */}
                <label className="af-consent-check" style={{ cursor: "pointer" }}>
                  <span
                    className={`af-consent-checkbox ${consentChecked ? "af-consent-checkbox--checked" : ""}`}
                    onClick={() => setConsentChecked((v) => !v)}
                  >
                    {consentChecked && (
                      <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                        <path d="M1 5l3.5 3.5L11 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </span>
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                  />
                  <span>I have read and agree to the Consent &amp; Agreement, Privacy Policy, and No Refund Policy.</span>
                </label>
              </div>
            </div>

            <div className="pb-modal-footer">
              <div className="pb-footer-actions">
                <button className="pb-cancel-btn" onClick={() => setStep(1)}>Back</button>
                <button
                  className="pb-submit-btn"
                  onClick={handleConsentNext}
                  disabled={!consentChecked}
                  style={{
                    opacity: consentChecked ? 1 : 0.42,
                    cursor: consentChecked ? "pointer" : "not-allowed",
                  }}
                >
                  I Agree
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── STEP 3 — Payment ── */}
        {step === 3 && (
          <>
            <div className="pb-modal-body">
              <div className="af-payment-grid">
                 <div className="af-summary-card">
                   <h3 className="af-section-label">Booking Summary</h3>
                   <div className="af-summary-rows">
                     <div className="af-summary-row"><span>Applicant</span><strong>{applicant.firstName} {applicant.lastName}</strong></div>
                     <div className="af-summary-row"><span>Phone</span><strong>{applicant.phoneCode} {applicant.phone}</strong></div>
                     <div className="af-summary-row"><span>Appointment Date</span><strong>{apptDate}</strong></div>
                     <div className="af-summary-row"><span>Appointment Time</span><strong>{apptTime}</strong></div>
                     <div className="af-summary-row">
                       <span>Booking Type</span>
                       <strong>{immediate ? "Immediate / Premium (T+1)" : "Standard"}</strong>
                     </div>
                     <div className="af-summary-divider" />
                     <div className="af-summary-row"><span>Passport Buddy Fee</span><strong>Rs. {buddyServiceFee.toLocaleString("en-IN")}</strong></div>
                      {payMethod !== "reward" && rewardDiscountAmount > 0 && (
                        <div className="af-summary-row"><span>Discount Applied</span><strong style={{ color: "#16a34a" }}>− Rs. {rewardDiscountAmount.toLocaleString("en-IN")}</strong></div>
                      )}
                      <div className="af-summary-row"><span>GST (18%)</span><strong>Rs. {gst.toLocaleString("en-IN")}</strong></div>
                      <div className="af-summary-row"><span>Convenience Fee</span><strong>{convFee === 0 ? "Free" : `Rs. ${convFee}`}</strong></div>
                      {payMethod === "reward" ? (
                        <>
                          <div className="af-summary-row"><span>Pay by Reward</span><strong style={{ color: "#16a34a" }}>− Rs. {total.toLocaleString("en-IN")}</strong></div>
                          <div className="af-summary-divider" />
                          <div className="af-summary-row af-summary-row--total"><span>Total</span><strong>Rs. 0</strong></div>
                        </>
                      ) : (
                        <>
                          <div className="af-summary-divider" />
                          <div className="af-summary-row af-summary-row--total"><span>Grand Total</span><strong>Rs. {total.toLocaleString("en-IN")}</strong></div>
                        </>
                      )}
                   </div>
                 </div>

                 <div className="af-payment-card">
                   <PaymentForm
                     selectedMethod={payMethod}
                     onMethodSelect={handleMethodSelect}
                     details={paymentDetails}
                     onDetailChange={setPayF}
                     walletBalances={walletBalances}
                     requiredAmount={total}
                     onRewardPay={handlePaymentSubmit}
                   />

                   <RewardDiscountSection
                     selectedMethod={payMethod}
                     baseApplicationFee={buddyServiceFee}
                     walletBalances={walletBalances}
                     discountAmount={rewardDiscountAmount}
                     onDiscountChange={setRewardDiscountAmount}
                     onOpenLogin={() => { setShowLoginGate(true); setRewardDiscountAmount(0); }}
                   />

                   {errors._general && <p className="pb-error" style={{ marginTop: "10px", textAlign: "center" }}>{errors._general}</p>}

                   <div className="af-payment-note" style={{ marginTop: "16px" }}>
                     <p>Your payment is secured. After payment, our agent will contact you within 24 hours for your appointment on <strong>{apptDate}</strong> at <strong>{apptTime}</strong>.</p>
                   </div>

                   <div className="af-pay-actions">
                     <button className="pb-cancel-btn af-back-btn" onClick={() => setStep(2)}>
                       Back 
                     </button>
                     <button className="pg-btn-primary af-pay-btn pg-btn-lg" onClick={handlePaymentSubmit} disabled={!payMethod}>
                       Pay NOW
                     </button>
                   </div>
                 </div>

                 <LoginGate
                   open={showLoginGate}
                   onClose={() => setShowLoginGate(false)}
                   onSuccess={() => {}}
                 />
              </div>
            </div>
          </>
        )}

         {/* ── STEP 4 — Success ── */}
         {step === 4 && (
           <>
             <div className="pb-modal-header">
               <div>
                 <h2 className="pb-modal-title">Booking Confirmed</h2>
                 <p className="pb-modal-sub">Your Passport Buddy is booked.</p>
               </div>
            <button className="pb-close" onClick={onClose}>&times;</button>
             </div>
             <div className="pb-modal-body">
               <div className="pb-success">
                 {/* <div className="pb-success-icon">
                   <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                     <circle cx="28" cy="28" r="28" fill="#111" />
                     <path d="M16 28l9 9 15-18" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                   </svg>
                 </div> */}
                 {/* <h2 className="pb-success-title">Passport Buddy Booked!</h2> */}
                 <p className="pb-success-msg">
                   Your Passport Buddy has been confirmed. Our agent will contact <strong>{applicant.firstName} {applicant.lastName}</strong> at <strong>{applicant.phoneCode} {applicant.phone}</strong> within 24 hours to finalise details for your appointment on <strong>{apptDate}</strong> at <strong>{apptTime}</strong>.
                 </p>
                 <div className="af-receipt-summary" style={{ maxWidth: "420px", width: "100%" }}>
                   <div className="af-receipt-row"><span>Booking ID</span><strong>{buddyId}</strong></div>
                   <div className="af-receipt-row"><span>Applicant</span><strong>{applicant.firstName} {applicant.lastName}</strong></div>
                   <div className="af-receipt-row"><span>Booking Type</span><strong>{immediate ? "Immediate / Premium (T+1)" : "Standard"}</strong></div>
                   <div className="af-receipt-row"><span>Service</span><strong>Passport Buddy</strong></div>
                   <div className="af-receipt-row"><span>Country</span><strong>{applicant.country}</strong></div>
                   <div className="af-receipt-row">
                     <span>Payment Mode</span>
                     <strong>{payMethod === "reward" ? "Pay by Reward" : payMethod === "upi" ? "UPI" : payMethod === "netbanking" ? "Net Banking" : payMethod === "debit" ? "Debit Card" : payMethod === "credit" ? "Credit Card" : payMethod}</strong>
                   </div>
                    <div className="af-receipt-row"><span>Date</span><strong>{bookingDate}</strong></div>
                    <div className="af-receipt-row"><span>Time</span><strong>{bookingTime}</strong></div>
                    <div className="af-receipt-row"><span>Appointment Date</span><strong>{apptDate}</strong></div>
                    <div className="af-receipt-row"><span>Appointment Time</span><strong>{apptTime}</strong></div>
                    <div className="af-receipt-row"><span>Service Fee</span><strong>Rs. {buddyServiceFee.toLocaleString("en-IN")}</strong></div>
                    <div className="af-receipt-row"><span>GST (18%)</span><strong>Rs. {gst.toLocaleString("en-IN")}</strong></div>
                    <div className="af-receipt-row"><span>Convenience Fee</span><strong>{convFee === 0 ? "Free" : `Rs. ${convFee}`}</strong></div>
                    {payMethod === "reward" ? (
                      <div className="af-receipt-row">
                        <span>Pay by Reward</span>
                        <strong style={{ color: "#dc2626" }}>− Rs. {total.toLocaleString("en-IN")}</strong>
                      </div>
                    ) : rewardDiscountAmount > 0 ? (
                      <div className="af-receipt-row">
                        <span>Reward Discount</span>
                        <strong style={{ color: "#16a34a" }}>− Rs. {rewardDiscountAmount.toLocaleString("en-IN")}</strong>
                      </div>
                    ) : null}
                    {payMethod === "upi" && paymentDetails.upiId && <div className="af-receipt-row"><span>UPI ID</span><strong>{paymentDetails.upiId}</strong></div>}
                    {payMethod === "reward" ? (
                      <div className="af-receipt-row af-receipt-row--total"><span>Total</span><strong>Rs. 0</strong></div>
                    ) : (
                      <div className="af-receipt-row af-receipt-row--total"><span>Total Paid</span><strong>Rs. {total.toLocaleString("en-IN")}</strong></div>
                    )}
                 </div>
                 {/* <p className="pb-success-count">1 Passport Buddy session booked.</p> */}
               </div>
             </div>

            {/* Footer with Download + Close always in view */}
            <div className="pb-modal-footer pb-modal-footer--success">
              <div className="pb-footer-actions pb-footer-actions--success">
                <button className="pb-submit-btn" onClick={downloadReceipt}>Download Receipt (PDF)</button>
                <button className="pb-cancel-btn" onClick={onClose}>Close</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// TrackApplication.js - Application tracking and re-application
// =============================================================================


const LoginGateTA = LoginGate;

function deductWalletDiscountTA(email, discountAmount, description, totalAmount) {
  if (!email || discountAmount <= 0) return { success: false };
  const wallet = getWallet(email);
  const totalBalance = (wallet.redeemable?.balance || 0) + (wallet.nonRedeemable?.balance || 0);
  if (totalBalance < discountAmount) return { success: false, wallet };

  let remaining = discountAmount;
  const redeemableUsed = Math.min(wallet.redeemable?.balance || 0, remaining);
  if (redeemableUsed > 0) {
    wallet.redeemable.balance -= redeemableUsed;
    remaining -= redeemableUsed;
    wallet.redeemable.transactions = wallet.redeemable.transactions || [];
    wallet.redeemable.transactions.unshift({
      id: `discount_${Date.now()}_r`,
      label: description || "Reward Discount Applied",
      amount: -redeemableUsed,
      type: "debit",
      date: new Date().toISOString(),
      note: "Applied as discount on passport re-application service fee",
      totalPrice: totalAmount || 0,
    });
  }

  if (remaining > 0) {
    const nonRedeemableUsed = Math.min(wallet.nonRedeemable?.balance || 0, remaining);
    if (nonRedeemableUsed > 0) {
      wallet.nonRedeemable.balance -= nonRedeemableUsed;
      wallet.nonRedeemable.transactions = wallet.nonRedeemable.transactions || [];
      wallet.nonRedeemable.transactions.unshift({
        id: `discount_${Date.now()}_nr`,
        label: description || "Reward Discount Applied",
        amount: -nonRedeemableUsed,
        type: "debit",
        date: new Date().toISOString(),
        note: "Applied as discount on passport re-application service fee",
        totalPrice: totalAmount || 0,
      });
    }
  }

  saveWallet(email, wallet);
  return { success: true, wallet };
}

const DEMO_APPLICATIONS = {
  "TRK-DEMO001": {
    trackId: "TRK-DEMO001",
    applicationId: "TVC-M1A2B3-XY9Z",
    serviceLabel: "New Passport — Normal Processing",
    applicants: [{ name: "Rahul Sharma", phone: "9876543210", country: "India", address: "42, Gandhi Nagar, New Delhi – 110001" }],
    passportBuddy: false,
    status: "appointment_booked",
    submittedAt: "12 Apr 2026, 10:32 AM",
    appointmentDate: "22 Apr 2026",
    appointmentTime: "10:30 AM",
    appointmentCenter: "PSK Connaught Place, New Delhi",
    filingStatus: "Filed on Passport Seva Portal",
    filedAt: "14 Apr 2026",
    policeVerification: "Pending (after issuance)",
    dispatchStatus: "Awaiting Appointment",
    stepIndex: 2,
    type: "application",
  },
  "TRK-DEMO002": {
    trackId: "TRK-DEMO002",
    applicationId: "TVC-N4C5D6-AB3K",
    serviceLabel: "Passport Renewal — Tatkaal Processing",
    applicants: [
      { name: "Priya Verma", phone: "9123456780", country: "India", address: "7B, Lake View Apartments, Andheri West, Mumbai – 400053" },
      { name: "Arjun Verma", phone: "9876512340", country: "India", address: "7B, Lake View Apartments, Andheri West, Mumbai – 400053" },
    ],
    passportBuddy: true,
    status: "processing",
    submittedAt: "10 Apr 2026, 03:15 PM",
    appointmentDate: "18 Apr 2026",
    appointmentTime: "02:00 PM",
    appointmentCenter: "POPSK Andheri West, Mumbai",
    filingStatus: "Filed on Passport Seva Portal",
    filedAt: "11 Apr 2026",
    policeVerification: "Completed (post-issuance track)",
    dispatchStatus: "Processing at RPO",
    stepIndex: 3,
    type: "application",
  },
  "TRK-DEMO003": {
    trackId: "TRK-DEMO003",
    applicationId: "TVC-E7F8G9-QR7M",
    serviceLabel: "Reissue — Lost Passport",
    applicants: [{ name: "Ananya Krishnan", phone: "9988776655", country: "India", address: "15, MG Road, Koramangala, Bengaluru – 560034" }],
    passportBuddy: false,
    status: "dispatched",
    submittedAt: "01 Apr 2026, 11:00 AM",
    appointmentDate: "05 Apr 2026",
    appointmentTime: "11:00 AM",
    appointmentCenter: "PSK Koramangala, Bengaluru",
    filingStatus: "Filed on Passport Seva Portal",
    filedAt: "02 Apr 2026",
    policeVerification: "Completed",
    dispatchStatus: "Dispatched via India Post Speed Post — EMS123456789IN",
    stepIndex: 4,
    type: "application",
  },
};

const STEPS = [
  { label: "Application Received" },
  { label: "Form Filed on Portal" },
  { label: "Appointment Booked" },
  { label: "Center Visit Done" },
  { label: "Passport Dispatched" },
];

const REAPPLY_STEPS = [
  { label: "Re-Application Received" },
  { label: "New Filing Initiated" },
  { label: "New Appointment Booked" },
  { label: "Center Visit Done" },
  { label: "Passport Dispatched" },
];

const STATUS_BADGE_MAP = {
  appointment_booked: { text: "Appointment Booked", cls: "trk-status-badge--appointment_booked" },
  processing:         { text: "Processing at RPO",   cls: "trk-status-badge--processing"         },
  dispatched:         { text: "Dispatched",           cls: "trk-status-badge--dispatched"         },
  received:           { text: "Passport Received",    cls: "trk-status-badge--received"           },
  reapply_received:   { text: "Re-Application Received", cls: "trk-status-badge--appointment_booked" },
};

let REAPPLY_SERVICE_OPTIONS = [];

const PAYMENT_MODES = [
  { id: "upi",        label: "UPI (GPay / PhonePe / Paytm)", convFlat: 0,  convPercent: 0   },
  { id: "netbanking", label: "Net Banking",                   convFlat: 15, convPercent: 0   },
  { id: "card",       label: "Credit / Debit Card",           convFlat: 25, convPercent: 1.5 },
];

const BUDDY_FEE = 1999;

function findApplication(trackId) {
  const key = trackId.trim().toUpperCase();
  if (DEMO_APPLICATIONS[key]) return DEMO_APPLICATIONS[key];
  const live = getApplication(key);
  if (live) return live;
  return null;
}

function generateReapplyId() {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `TVC-RA-${ts}-${rand}`;
}

function generateReapplyTrackId() {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `TRK-RA-${ts}-${rand}`;
}

export function TrackApplication({ onClose }) {
  const [trackInput, setTrackInput]   = useState("");
  const [result, setResult]           = useState(null);
  const [notFound, setNotFound]       = useState(false);

  const [reapplyStep, setReapplyStep] = useState(0);
  const [selectedMap, setSelectedMap] = useState({});
  const [reapplyService, setReapplyService] = useState("");
  const [reapplyBuddy, setReapplyBuddy]     = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [paymentMode, setPaymentMode]       = useState("upi");

  const [bankName, setBankName]     = useState("");
  const [accNumber, setAccNumber]   = useState("");
  const [ifsc, setIfsc]             = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv]       = useState("");
  const [cardName, setCardName]     = useState("");
  const [upiId, setUpiId]           = useState("");
  const [payErrors, setPayErrors]   = useState({});
  const [reapplyPayMethod, setReapplyPayMethod] = useState("debit");
  const [reapplyPaymentDetails, setReapplyPaymentDetails] = useState(PAYMENT_DETAILS_DEFAULT);
  const setReapplyPayF = (e) => setReapplyPaymentDetails(d => ({ ...d, [e.target.name]: e.target.value }));
  const [showLoginGateTA, setShowLoginGateTA] = useState(false);
  const [rewardDiscountAmount, setRewardDiscountAmount] = useState(0);

  const [reapplyData, setReapplyData] = useState(null);

  const { user: session } = useAuth();
  const isLoggedIn = !!session?.email;
  const walletBalances = isLoggedIn ? getCurrentWalletBalances(session?.email) : null;

  const selectedApplicants = result
    ? result.applicants.filter((a) => selectedMap[a.name])
    : [];
  const reapplyCount = selectedApplicants.length;
  const reapplySelectedService = REAPPLY_SERVICE_OPTIONS.find((s) => s.id === reapplyService);
  const payModeForFee = paymentMode === "upi" ? "upi" : paymentMode === "card" ? "card" : paymentMode === "netbanking" ? "netbanking" : (reapplyPayMethod === "debit" || reapplyPayMethod === "credit" ? "card" : reapplyPayMethod === "upi" ? "upi" : reapplyPayMethod === "netbanking" ? "netbanking" : "upi");
  const selectedPM             = PAYMENT_MODES.find((p) => p.id === payModeForFee);

  const reapplyServiceFee = reapplySelectedService ? reapplySelectedService.fee * reapplyCount : 0;
  const maxRewardDiscount = Math.round(reapplyServiceFee * REWARD_DISCOUNT_MAX_RATE);
  const reapplyFilingFee  = 499 * reapplyCount;
  const reapplyBuddyFee   = reapplyBuddy ? BUDDY_FEE : 0;
  const reapplySubtotal   = reapplyFilingFee + reapplyServiceFee + reapplyBuddyFee;
  const reapplyGst        = Math.round(reapplySubtotal * 0.18);
  const convFee           = selectedPM
    ? selectedPM.convFlat + Math.round(reapplySubtotal * selectedPM.convPercent / 100)
    : 0;
  const reapplyTotal      = reapplySubtotal + reapplyGst + convFee - rewardDiscountAmount;

  const handleSearch = () => {
    const found = findApplication(trackInput);
    if (found) {
      setResult(found);
      setNotFound(false);
      resetReapply(found);
    } else {
      setResult(null);
      setNotFound(true);
    }
  };
  const handleKeyDown = (e) => { if (e.key === "Enter") handleSearch(); };

  const resetReapply = (app) => {
    setReapplyStep(0);
    setReapplyService("");
    setReapplyBuddy(false);
    setConsentChecked(false);
    setPaymentMode("upi");
    setPayErrors({});
    setUpiId(""); setBankName(""); setAccNumber(""); setIfsc("");
    setCardNumber(""); setCardExpiry(""); setCardCvv(""); setCardName("");
    setReapplyPayMethod("debit");
    setReapplyPaymentDetails(PAYMENT_DETAILS_DEFAULT);
    setRewardDiscountAmount(0);
    setReapplyData(null);
    if (app) {
      const map = {};
      app.applicants.forEach((a) => { map[a.name] = false; });
      setSelectedMap(map);
    }
  };

  const toggleApplicant = (name) => {
    setSelectedMap((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleOpenReapply = () => { resetReapply(result); setReapplyStep(1); };

  const handleStep1Next = () => {
    if (reapplyCount === 0) { alert("Please select at least one applicant."); return; }
    if (!reapplyService)   { alert("Please select a service type."); return; }
    setReapplyStep(2);
  };

  const handleConsentNext = () => {
    if (!consentChecked) return;
    setReapplyStep(3);
  };

  const validatePayment = () => {
    const e = {};
    if (paymentMode === "upi") {
      if (!upiId.trim()) e.upiId = "UPI ID is required.";
      else if (!/^[a-zA-Z0-9@._-]+$/.test(upiId.trim()) || !upiId.includes("@"))
        e.upiId = "Enter a valid UPI ID (e.g. mobilenumber@upi).";
    }
    if (paymentMode === "netbanking") {
      if (!bankName.trim())   e.bankName   = "Bank name is required.";
      if (!accNumber.trim())  e.accNumber  = "Account number is required.";
      if (!ifsc.trim())       e.ifsc       = "IFSC code is required.";
    }
    if (paymentMode === "card") {
      if (!/^\d{16}$/.test(cardNumber.replace(/\s/g, ""))) e.cardNumber = "Enter a valid 16-digit card number.";
      if (!/^\d{2}\/\d{2}$/.test(cardExpiry))              e.cardExpiry = "Enter expiry as MM/YY.";
      if (!/^\d{3,4}$/.test(cardCvv))                      e.cardCvv    = "Enter a valid CVV.";
      if (!cardName.trim())                                  e.cardName   = "Name on card is required.";
    }
    return e;
  };

  const handlePaymentSubmit = () => {
    if (reapplyPayMethod === "reward") {
      if (!isLoggedIn || !session?.email) {
        setPayErrors({ _general: "Please sign in to use Pay by Reward." });
        return;
      }
      if (walletBalances && reapplyTotal > walletBalances.total) {
        setPayErrors({ _general: `Insufficient wallet balance. You need ₹${reapplyTotal.toLocaleString("en-IN")} but have ₹${walletBalances.total.toLocaleString("en-IN")}.` });
        return;
      }
      const result = spendRewardMoney(session.email, reapplyTotal, "Passport Re-Application — Pay by Reward");
      if (!result.success) {
        setPayErrors({ _general: "Failed to deduct reward balance. Please try again." });
        return;
      }
      setPayErrors({});
    } else {
      if (rewardDiscountAmount > 0) {
        if (!isLoggedIn) {
          setPayErrors({ _general: "Please sign in to use reward discounts." });
          return;
        }
        if (rewardDiscountAmount > maxRewardDiscount) {
          setPayErrors({ _general: `Discount cannot exceed ${Math.round(REWARD_DISCOUNT_MAX_RATE * 100)}% of the service fee (₹${maxRewardDiscount.toLocaleString("en-IN")}).` });
          return;
        }
        if (walletBalances && rewardDiscountAmount > walletBalances.total) {
          setPayErrors({ _general: `Insufficient wallet balance. You have ₹${walletBalances.total.toLocaleString("en-IN")}.` });
          return;
        }
      }

      const err = validatePaymentDetails(reapplyPayMethod, reapplyPaymentDetails);
      if (err) { setPayErrors({ _general: err }); return; }
      setPayErrors({});

      if (rewardDiscountAmount > 0 && session?.email) {
        deductWalletDiscountTA(session.email, rewardDiscountAmount, "Passport Re-Application Discount", reapplyTotal + rewardDiscountAmount);
      }
    }

    const now = new Date();
    const newTrackId = generateReapplyTrackId();
    const newReapplyId = generateReapplyId();

    const data = {
      reapplyId: newReapplyId,
      newTrackId,
      trackId: result.trackId,
      applicationId: result.applicationId,
      applicants: selectedApplicants,
      service: reapplySelectedService,
      passportBuddy: reapplyBuddy,
      paymentMode: reapplyPayMethod === "reward" ? "Pay by Reward" : reapplyPayMethod,
      rewardUsed: reapplyPayMethod === "reward" ? reapplyTotal : rewardDiscountAmount,
      upiId: reapplyPaymentDetails.upiId || "",
      filingFee: reapplyFilingFee,
      serviceFee: reapplyServiceFee,
      buddyFee: reapplyBuddyFee,
      subtotal: reapplySubtotal,
      gst: reapplyGst,
      convFee,
      rewardDiscount: reapplyPayMethod === "reward" ? reapplyTotal : rewardDiscountAmount,
      total: reapplyTotal,
      submittedAt: now.toLocaleString("en-IN"),
    };

    const reapplyTrackEntry = {
      trackId: newTrackId,
      applicationId: result.applicationId,
      serviceLabel: reapplySelectedService.label,
      applicants: selectedApplicants,
      passportBuddy: reapplyBuddy,
      status: "reapply_received",
      submittedAt: now.toLocaleString("en-IN"),
      appointmentDate: "To be confirmed within 24 hours",
      appointmentTime: "To be confirmed",
      appointmentCenter: "Nearest PSK / POPSK to your address",
      filingStatus: "Re-Application Received — Processing Begins",
      filedAt: "Pending",
      policeVerification: "Pending",
      dispatchStatus: "Awaiting new appointment confirmation",
      stepIndex: 1,
      type: "reapplication",
      originalTrackId: result.trackId,
      reapplyId: newReapplyId,
    };

    saveApplication(newTrackId, reapplyTrackEntry);
    const payMethodLabel = PAYMENT_METHODS_LIST.find(m => m.id === reapplyPayMethod)?.label;
    savePassportBooking({
      id: newReapplyId || newTrackId,
      type: "passport-reapplication",
      paymentMethod: reapplyPayMethod === "reward" ? "Pay by Reward" : (payMethodLabel || reapplyPayMethod),
      totalAmount: reapplyTotal,
      rewardDeduction: reapplyPayMethod === "reward" ? reapplyTotal : (rewardDiscountAmount || 0),
      bookedAt: now.toISOString(),
    }, session?.email);

    setReapplyData(data);
    setReapplyStep(4);
  };

  const downloadReceipt = async (data) => {
    try {
      const { jsPDF } = await import("jspdf");
      const html2canvas = (await import("html2canvas")).default;
      const toBase64 = async (url) => {
        const resp = await fetch(url);
        const blob = await resp.blob();
        return new Promise((r) => {
          const reader = new FileReader();
          reader.onloadend = () => r(reader.result);
          reader.readAsDataURL(blob);
        });
      };
      let logoSrc = null;
      try { logoSrc = await toBase64(receiptLogo); } catch { logoSrc = null; }

      const applicantLines = data.applicants.map((a) => `Name: ${a.name}, Phone: +91 ${a.phone}`).join("<br>");

      const isPayByReward = data.paymentMode === "Pay by Reward";
      const discountAmt = isPayByReward ? data.total : (data.rewardDiscount || 0);

      const item = {
        label: "Re-Application",
        countryName: applicantLines,
        price: data.serviceFee + data.filingFee + data.buddyFee,
        data: `${data.service.label}${data.passportBuddy ? " + Passport Buddy" : ""}`,
        validity: 1,
        type: "service",
      };

      const methodMap = { upi: "upi", netbanking: "netbanking", debit: "debit", credit: "credit" };
      const payMethodVal = isPayByReward ? "reward" : (methodMap[data.paymentMode] || "upi");

      const html = generateReceiptHTML({
        item, quantity: 1, payMethod: payMethodVal,
        billingDetails: {
          firstName: data.applicants[0]?.name || "",
          lastName: "",
          email: "",
          country: "",
          phone: data.applicants[0]?.phone || "",
          whatsapp: "",
          phoneCode: "+91",
          address: "",
          city: "",
          pincode: "",
        },
         orderId: data.reapplyId,
         currency: "INR",
         appliedDiscount: discountAmt,
         logoSrc, pdf: true, inr: true,
        upiId: isPayByReward ? "" : (data.upiId || ""),
        bank: isPayByReward ? "Pay by Reward" : (data.paymentMode === "netbanking" ? (reapplyPaymentDetails.bankName || "") : ""),
        card: { number: !isPayByReward && (data.paymentMode === "debit" || data.paymentMode === "credit") ? (reapplyPaymentDetails.cardNum || "") : "" },
        simType: "",
        overrides: {
          subtotal: data.serviceFee + data.filingFee + data.buddyFee,
          tax: data.gst,
          convFee: data.convFee,
          total: isPayByReward ? 0 : data.total,
          discountLabel: isPayByReward ? "Pay by Reward" : "Discount",
          hideSubtotal: true,
          extraRows: [
            { label: "Filing Fee", value: data.filingFee },
            { label: "Service Fee", value: data.serviceFee },
            ...(data.buddyFee ? [{ label: "Passport Buddy", value: data.buddyFee }] : []),
          ],
        },
      });

      const container = document.createElement("div");
      container.innerHTML = html;
      container.style.position = "fixed";
      container.style.left = "-9999px";
      container.style.top = "0";
      container.style.width = "210mm";
      container.style.background = "#f5f9ff";
      document.body.appendChild(container);
      const el = container.querySelector(".Receipt-card") || container.firstElementChild;
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: "#f5f9ff" });
      document.body.removeChild(container);

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`TheVisaCo-ReApply-${data.reapplyId}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    }
  };

  const formatCard   = (v) => v.replace(/\D/g, "").replace(/(.{4})/g, "$1 ").trim().slice(0, 19);
  const formatExpiry = (v) => { const d = v.replace(/\D/g, "").slice(0, 4); return d.length > 2 ? d.slice(0, 2) + "/" + d.slice(2) : d; };

  const isReapplication = result && result.type === "reapplication";
  const stepsToShow = isReapplication ? REAPPLY_STEPS : STEPS;

  const renderStep1 = () => (
    <div className="trk-reapply-panel">
      <h4>Re-Application Request</h4>

      <div className="trk-reapply-grid">
        <div className="trk-reapply-grid__left">
          <p style={{ fontSize: "12px", color: "var(--ink-light)", marginBottom: "8px", fontWeight: 300, lineHeight: 1.5 }}>
            {result.applicants.length === 1
              ? "Confirm re-application for this applicant and choose a new service type."
              : "Select each applicant who needs to re-apply. Fees are charged per person selected."}
          </p>
          <div className="trk-reapply-grid__left-card">
          {result.applicants.map((a, idx) => (
            <label key={a.name} style={{
              display: "flex", alignItems: "center", gap: "14px", padding: "14px 18px",
              borderBottom: idx < result.applicants.length - 1 ? "1px solid var(--border-light)" : "none",
              cursor: "pointer",
              background: selectedMap[a.name] ? "var(--brand-faint)" : "var(--bg)",
              transition: "background 0.15s",
            }}>
              <input type="checkbox" checked={!!selectedMap[a.name]} onChange={() => toggleApplicant(a.name)}
                style={{ accentColor: "var(--brand)", width: "16px", height: "16px", flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <span style={{ fontFamily: "var(--font-body)", fontSize: "14px", fontWeight: selectedMap[a.name] ? 600 : 400, color: selectedMap[a.name] ? "var(--brand)" : "var(--ink-mid)", display: "block" }}>Applicant {idx + 1}</span>
              </div>
              {selectedMap[a.name] && (
                <span style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--brand)", background: "rgba(26,58,92,0.1)", padding: "3px 8px", borderRadius: "var(--radius)" }}>Selected</span>
              )}
            </label>
          ))}
          {result.applicants.length > 1 && (
            <p style={{ fontSize: "12px", color: "var(--ink-light)", padding: "8px 18px 14px", fontWeight: 300, lineHeight: 1.5, margin: 0 }}>
              {reapplyCount === 0 ? "No applicants selected yet." : `${reapplyCount} applicant${reapplyCount > 1 ? "s" : ""} selected.`}
            </p>
          )}
          </div>
        </div>

        <div className="trk-reapply-grid__middle">
          <label className="pb-label">New Service Type *</label>
          <select className="pb-input af-select" value={reapplyService} onChange={(e) => setReapplyService(e.target.value)}>
            <option value="">— Choose Service —</option>
            {REAPPLY_SERVICE_OPTIONS.map((s) => (
              <option key={s.id} value={s.id}>{s.label} (Service Fee: Rs. {s.fee}/person)</option>
            ))}
          </select>
          <div className="af-buddy-toggle" style={{ margin: "10px 0 0" }}>
            <label className="af-buddy-label">
              <div className="af-buddy-toggle-info">
                <span className="af-buddy-title">Add Passport Buddy — Optional</span>
                <span className="af-buddy-desc">
                  Rs. {BUDDY_FEE.toLocaleString("en-IN")} — Agent accompanies you at the passport center
                </span>
              </div>
              <div className="af-toggle-wrap">
                <input type="checkbox" checked={reapplyBuddy} onChange={(e) => setReapplyBuddy(e.target.checked)} className="af-toggle-input" />
                <span className={`af-toggle-switch ${reapplyBuddy ? "af-toggle-switch--on" : ""}`}></span>
              </div>
            </label>
          </div>
        </div>
      </div>

      {reapplyCount > 0 && reapplyService && (
        <div className="af-price-preview" style={{ marginBottom: "18px" }}>
          <h3 className="af-section-label">Fee Estimate</h3>
          <div className="af-price-rows">
            <div className="af-price-row">
              <span>Filing Fee ({reapplyCount} × Rs. 499)</span>
              <strong>Rs. {reapplyFilingFee.toLocaleString("en-IN")}</strong>
            </div>
            <div className="af-price-row">
              <span>Service Fee ({reapplyCount} × Rs. {reapplySelectedService?.fee})</span>
              <strong>Rs. {reapplyServiceFee.toLocaleString("en-IN")}</strong>
            </div>
            {reapplyBuddy && (
              <div className="af-price-row">
                <span>Passport Buddy</span>
                <strong>Rs. {reapplyBuddyFee.toLocaleString("en-IN")}</strong>
              </div>
            )}
            <div className="af-price-row">
              <span>GST (18%)</span>
              <strong>Rs. {reapplyGst.toLocaleString("en-IN")}</strong>
            </div>
            <div className="af-price-row af-price-row--total">
              <span>Total </span>
              <strong>Rs. {(reapplySubtotal + reapplyGst).toLocaleString("en-IN")}</strong>
            </div>
          </div>
        </div>
      )}

      <div className="trk-reapply-actions">
        <button className="pb-cancel-btn" onClick={() => setReapplyStep(0)}>Cancel</button>
        <button className="pb-submit-btn" onClick={handleStep1Next}
          disabled={reapplyCount === 0 || !reapplyService}
          style={{ opacity: (reapplyCount === 0 || !reapplyService) ? 0.5 : 1, cursor: (reapplyCount === 0 || !reapplyService) ? "not-allowed" : "pointer" }}>
          {reapplyCount > 0 && reapplyService ? "Proceed " : "Select Applicant "}
        </button>
      </div>
    </div>
  );

  const renderConsentStep = () => (
    <div className="trk-reapply-panel">
      <h4>Consent &amp; Agreement</h4>
      <div className="af-consent-box">
        <div className="af-consent-scroll">
          <h4>Consent &amp; Agreement — TheVisaCo Re-Application Service</h4>
          <p>By proceeding with this re-application, you agree to the following terms:</p>
          <h5>1. User Consent for Information Collection</h5>
          <p>You agree to provide accurate, complete, and up-to-date personal information. You authorise us to use this information solely for processing your passport re-application.</p>
          <h5>2. Document Upload &amp; Verification</h5>
          <p>All documents are genuine and belong to you. Any incorrect or fraudulent submission is solely your responsibility.</p>
          <h5>3. Service Scope</h5>
          <p>Our services include assistance with re-application, appointment booking, form filling, and general guidance. We act as a facilitation service and are not affiliated with any government authority.</p>
          <h5>4. Appointment Booking</h5>
          <p>Appointment availability depends on official passport systems and cannot be guaranteed. We will make best efforts to secure the earliest available slot.</p>
          <h5>5. Payment Terms</h5>
          <p>All service fees must be paid in advance. Payments once made are non-refundable under any circumstances.</p>
          <h5>6. No Refund Policy</h5>
          <p>No refunds will be provided for application rejection, government delays, incorrect user information, or change of mind after payment.</p>
          <h5>7. Liability Limitation</h5>
          <p>We are not responsible for government decisions, delays, or rejections. Our role is limited to assistance and facilitation.</p>
          {reapplyBuddy && (
            <>
              <h5>8. On-Ground Assistance (Passport Buddy)</h5>
              <p>Our agent accompanies you at the passport center for guidance only and has no authority over government officers' decisions.</p>
            </>
          )}
          <h5>{reapplyBuddy ? "9" : "8"}. Privacy &amp; Data Protection</h5>
          <p>Your data will be handled securely and used only for service purposes. We do not sell or share your personal information with third parties.</p>
          <h5>{reapplyBuddy ? "10" : "9"}. Agreement Acceptance</h5>
          <p>By clicking "I Agree", you confirm you have read and understood all terms and agree to be legally bound by this agreement.</p>
        </div>

        {/* Custom checkbox with visible tick */}
        <label className="af-consent-check" style={{ cursor: "pointer" }}>
          <span
            className={`af-consent-checkbox ${consentChecked ? "af-consent-checkbox--checked" : ""}`}
            onClick={() => setConsentChecked((v) => !v)}
          >
            {consentChecked && (
              <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                <path d="M1 5l3.5 3.5L11 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </span>
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => setConsentChecked(e.target.checked)}
            style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
          />
          <span>I have read and agree to the Consent &amp; Agreement, Privacy Policy, and No Refund Policy.</span>
        </label>
      </div>

      <div className="trk-reapply-actions" style={{ marginTop: "16px" }}>
        <button className="pb-cancel-btn" onClick={() => setReapplyStep(1)}>Back</button>
        <button
          className="pb-submit-btn"
          onClick={handleConsentNext}
          disabled={!consentChecked}
          style={{
            opacity: consentChecked ? 1 : 0.42,
            cursor: consentChecked ? "pointer" : "not-allowed",
          }}
        >
          I Agree
        </button>
      </div>
    </div>
  );

  const handleReapplyMethodSelect = (id) => {
    setReapplyPayMethod(id);
    setReapplyPaymentDetails(PAYMENT_DETAILS_DEFAULT);
  };

  const renderPaymentStep = () => (
    <div className="trk-reapply-panel">
      <h4>Payment</h4>
      <div className="af-payment-grid af-payment-grid--reapply">
         <div className="af-summary-card af-summary-card--reapply">
           <h3 className="af-section-label">Re-Application Summary</h3>
                       <div className="af-summary-rows">
             <h4 className="af-section-label" style={{ fontSize: "13px", marginBottom: "6px" }}>Applicant Details</h4>
             {selectedApplicants.map((a, i) => (
               <div className="af-summary-row" key={a.name}><span>Applicant {i + 1}</span><strong>{a.name}</strong></div>
             ))}
             <div className="af-summary-divider" />
             <div className="af-summary-row"><span>Service</span><strong>{reapplySelectedService?.label}</strong></div>
             <div className="af-summary-row"><span>Passport Buddy</span><strong>{reapplyBuddy ? "Yes — Included" : "Not Availed"}</strong></div>
             <div className="af-summary-divider" />
             <div className="af-summary-row"><span>Filing Fee ({reapplyCount} × Rs. 499)</span><strong>Rs. {reapplyFilingFee.toLocaleString("en-IN")}</strong></div>
             <div className="af-summary-row"><span>Service Fee ({reapplyCount} × Rs. {reapplySelectedService?.fee})</span><strong>Rs. {reapplyServiceFee.toLocaleString("en-IN")}</strong></div>
             {reapplyBuddy && <div className="af-summary-row"><span>Passport Buddy</span><strong>Rs. {reapplyBuddyFee.toLocaleString("en-IN")}</strong></div>}
             <div className="af-summary-row"><span>GST (18%)</span><strong>Rs. {reapplyGst.toLocaleString("en-IN")}</strong></div>
             <div className="af-summary-row"><span>Convenience Fee</span><strong>{convFee === 0 ? "Free" : `Rs. ${convFee}`}</strong></div>
              {reapplyPayMethod !== "reward" && rewardDiscountAmount > 0 && (
                <div className="af-summary-row"><span>Discount Applied</span><strong style={{ color: "#16a34a" }}>− Rs. {rewardDiscountAmount.toLocaleString("en-IN")}</strong></div>
              )}
              {reapplyPayMethod === "reward" ? (
                <>
                  <div className="af-summary-row"><span>Pay by Reward</span><strong style={{ color: "#16a34a" }}>− Rs. {reapplyTotal.toLocaleString("en-IN")}</strong></div>
                  <div className="af-summary-divider" />
                  <div className="af-summary-row af-summary-row--total"><span>Total</span><strong>Rs. 0</strong></div>
                </>
              ) : (
                <>
                  <div className="af-summary-divider" />
                  <div className="af-summary-row af-summary-row--total"><span>Grand Total</span><strong>Rs. {reapplyTotal.toLocaleString("en-IN")}</strong></div>
                </>
              )}
           </div>
         </div>

         <div className="af-payment-card">
           <PaymentForm
             selectedMethod={reapplyPayMethod}
             onMethodSelect={handleReapplyMethodSelect}
             details={reapplyPaymentDetails}
             onDetailChange={setReapplyPayF}
             walletBalances={walletBalances}
             requiredAmount={reapplyTotal}
             onRewardPay={handlePaymentSubmit}
           />

           <RewardDiscountSection
             selectedMethod={reapplyPayMethod}
             baseApplicationFee={reapplyServiceFee}
             walletBalances={walletBalances}
             discountAmount={rewardDiscountAmount}
             onDiscountChange={setRewardDiscountAmount}
             onOpenLogin={() => { setShowLoginGateTA(true); setRewardDiscountAmount(0); }}
           />

           {payErrors._general && <p className="pb-error" style={{ marginTop: "10px", textAlign: "center" }}>{payErrors._general}</p>}

           <div className="af-payment-note" style={{ marginTop: "16px" }}>
              <p style={{ textAlign: "center" }}>Your payment is secured. You will receive a unique Track ID after payment to follow your re-application progress.</p>
           </div>

           <div className="af-pay-actions">
             <button className="pb-cancel-btn af-back-btn" onClick={() => setReapplyStep(2)}>
               Back
             </button>
             <button className="pg-btn-primary af-pay-btn pg-btn-lg" onClick={handlePaymentSubmit} disabled={!reapplyPayMethod}>
               Pay NOW
             </button>
           </div>
         </div>
      </div>
    </div>
  );

   const getPaymentModeLabel = (mode) => {
     if (mode === "Pay by Reward") return "Pay by Reward";
     if (mode === "upi") return "UPI";
     if (mode === "netbanking") return "Net Banking";
     if (mode === "debit") return "Debit Card";
     if (mode === "credit") return "Credit Card";
     return mode;
   };

    const renderSuccessStep = () => (
      <>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ textAlign: "center" }}>
            <h4>Re-Application Request Submitted</h4>
            <p>
              Re-application confirmed for{" "}
              <strong>{reapplyData.applicants.map((a) => a.name).join(", ")}</strong>.
            </p>
          </div>

        <div className="trk-reapply-success-grid">
         <div className="trk-reapply-success-grid__left">
           <div style={{
             background: "var(--brand-faint)",
             border: "1px solid rgba(26,58,92,0.18)",
             borderRadius: "var(--radius-md)",
             padding: "16px 20px",
           }}>
             <p style={{ fontFamily: "var(--font-body)", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--brand)", marginBottom: "6px" }}>
               Your New Track ID
             </p>
             <p style={{ fontFamily: "var(--font-body)", fontSize: "18px", fontWeight: 700, color: "var(--brand)", letterSpacing: "0.06em", marginBottom: "6px", wordBreak: "break-all" }}>
               {reapplyData.newTrackId}
             </p>
           </div>
            <div style={{
              background: "var(--brand-faint)",
              border: "1px solid rgba(26,58,92,0.18)",
              borderRadius: "var(--radius-md)",
              padding: "16px 20px",
            }}>
              <p style={{ fontFamily: "var(--font-body)", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--brand)", marginBottom: "4px" }}>
                Re-Application ID
              </p>
              <p style={{ fontFamily: "var(--font-body)", fontSize: "16px", fontWeight: 600, color: "var(--success)", letterSpacing: "0.04em", wordBreak: "break-all", margin: 0, marginBottom: reapplyData.passportBuddy ? "12px" : 0 }}>
                {reapplyData.reapplyId}
              </p>
              {reapplyData.passportBuddy && (
                <>
                  <div style={{ height: "1px", background: "rgba(26,58,92,0.12)", margin: "10px 0" }} />
                  <p style={{ fontFamily: "var(--font-body)", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--brand)", marginBottom: "4px" }}>
                    Passport Buddy
                  </p>
                  <p style={{ fontFamily: "var(--font-body)", fontSize: "13px", color: "var(--ink)", fontWeight: 400, lineHeight: 1.5, margin: 0 }}>
                    A Passport Buddy agent will be assigned for your new appointment.
                  </p>
                </>
              )}
            </div>
         </div>
         <div className="trk-reapply-success-grid__right">
            <div className="af-receipt-summary" style={{ maxWidth: "600px", width: "100%" }}>
             <div className="af-receipt-row"><span>Service</span><strong>{reapplyData.service.label}</strong></div>
             <div className="af-receipt-row"><span>Passport Buddy</span><strong>{reapplyData.passportBuddy ? "Yes — Included" : "Not Availed"}</strong></div>
             <div className="af-receipt-row">
               <span>Payment Mode</span>
               <strong>{getPaymentModeLabel(reapplyData.paymentMode)}</strong>
             </div>
             <div className="af-summary-divider" style={{ margin: "8px 0", borderBottom: "1px dashed var(--border-light)" }} />
             <div className="af-receipt-row"><span>Filing Fee</span><strong>Rs. {reapplyData.filingFee.toLocaleString("en-IN")}</strong></div>
             <div className="af-receipt-row"><span>Service Fee</span><strong>Rs. {reapplyData.serviceFee.toLocaleString("en-IN")}</strong></div>
             {reapplyData.passportBuddy && <div className="af-receipt-row"><span>Passport Buddy Fee</span><strong>Rs. {reapplyData.buddyFee.toLocaleString("en-IN")}</strong></div>}
             <div className="af-receipt-row"><span>GST (18%)</span><strong>Rs. {reapplyData.gst.toLocaleString("en-IN")}</strong></div>
              <div className="af-receipt-row"><span>Convenience Fee</span><strong>{reapplyData.convFee === 0 ? "Free" : `Rs. ${reapplyData.convFee.toLocaleString("en-IN")}`}</strong></div>
             {reapplyData.paymentMode === "Pay by Reward" ? (
               <>
                 {reapplyData.rewardUsed > 0 && (
                   <div className="af-receipt-row">
                     <span>Paid by Reward</span>
                     <strong style={{ color: "#dc2626" }}>− Rs. {reapplyData.rewardUsed.toLocaleString("en-IN")}</strong>
                   </div>
                 )}
                 <div className="af-summary-divider" style={{ margin: "8px 0", borderBottom: "1px dashed var(--border-light)" }} />
                 <div className="af-receipt-row af-receipt-row--total">
                   <span>Total </span>
                   <strong>Rs. 0</strong>
                 </div>
               </>
             ) : (
               <>
                 {reapplyData.rewardDiscount > 0 && (
                   <div className="af-receipt-row">
                     <span>Reward Discount</span>
                     <strong style={{ color: "#16a34a" }}>− Rs. {reapplyData.rewardDiscount.toLocaleString("en-IN")}</strong>
                   </div>
                 )}
                 <div className="af-summary-divider" style={{ margin: "8px 0", borderBottom: "1px dashed var(--border-light)" }} />
                 <div className="af-receipt-row af-receipt-row--total">
                   <span>Total </span>
                   <strong>Rs. {reapplyData.total.toLocaleString("en-IN")}</strong>
                 </div>
               </>
             )}
           </div>
         </div>
      </div>
      <div style={{ marginTop: "20px", display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap" }}>
        <button className="pb-submit-btn" onClick={() => downloadReceipt(reapplyData)}>
          Download Receipt (PDF)
        </button>
        <button className="pb-cancel-btn" onClick={() => {
          setTrackInput(reapplyData.newTrackId);
          setReapplyStep(0);
          const found = findApplication(reapplyData.newTrackId);
          if (found) { setResult(found); setNotFound(false); resetReapply(found); }
        }}>
          Track This Re-Application
        </button>
        {/* <button className="pb-cancel-btn" onClick={() => setReapplyStep(0)}>
          Close
        </button> */}
      </div>
      </div>
    </>
  );

  return (
    <div className="pb-overlay" onClick={onClose}>
      <div className="pb-modal pb-modal--wide pb-modal--track" onClick={(e) => e.stopPropagation()}>
        <div className="pb-modal-header">
          <div>
            <h2 className="pb-modal-title">Track Your Application</h2>
            <p className="pb-modal-sub">
              Enter your Track ID to check your appointment date, filing status, and dispatch updates.
            </p>
          </div>
          <button className="pb-close" onClick={onClose}>&times;</button>
        </div>

        <div className="pb-modal-body">
          <div className="trk-search-row">
            <div className="trk-search-pill">
              <svg className="trk-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                className="trk-search-input"
                placeholder="Enter your Track ID"
                value={trackInput}
                onChange={(e) => setTrackInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button className="trk-search-btn" onClick={handleSearch}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"/>
                  <path d="m12 5 7 7-7 7"/>
                </svg>
              </button>
            </div>
          </div>
          <p className="trk-demo-hint" style={{ textAlign: "center" }}>
            Demo IDs: <strong>TRK-DEMO001</strong> (Appointment Booked) &nbsp;·&nbsp; <strong>TRK-DEMO002</strong> (Processing, 2 applicants) &nbsp;·&nbsp; <strong>TRK-DEMO003</strong> (Dispatched).
          </p>

          {notFound && (
            <div className="trk-not-found">
              <p>No application found for this Track ID. Please double-check and try again.</p>
            </div>
          )}

          {result && (
            <div className="trk-result">
              {/* Re-application banner */}
              {isReapplication && (
                <div style={{
                  background: "#fffbee",
                  border: "1px solid rgba(184,104,0,0.2)",
                  borderRadius: "var(--radius-md)",
                  padding: "12px 18px",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: "var(--font-body)", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--warning)", marginBottom: "4px" }}>
                      Re-Application
                    </p>
                    <p style={{ fontFamily: "var(--font-body)", fontSize: "13px", color: "#7a5000", fontWeight: 300, lineHeight: 1.5 }}>
                      This is a re-application. Original application track ID:{" "}
                      <strong style={{ fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => {
                          setTrackInput(result.originalTrackId);
                          const found = findApplication(result.originalTrackId);
                          if (found) { setResult(found); resetReapply(found); }
                        }}>
                        {result.originalTrackId}
                      </strong>
                    </p>
                  </div>
                </div>
              )}

              <div className="trk-header">
                <div>
                  <span className="trk-tag">
                    {isReapplication
                      ? "Re-Application"
                      : result.passportBuddy ? "Passport Buddy Included" : "Standard Application"}
                  </span>
                  <h3 className="trk-name">
                    {result.applicants.length > 1
                      ? `${result.applicants[0].name} + ${result.applicants.length - 1} other${result.applicants.length > 2 ? "s" : ""}`
                      : result.applicants[0].name}
                  </h3>
                  <p className="trk-service">
                    {result.serviceLabel} &nbsp;·&nbsp; {result.applicants.length} applicant{result.applicants.length > 1 ? "s" : ""}
                  </p>
                </div>
                {!isReapplication && result.status !== "dispatched" ? (
                  <span className="trk-status-badge-wrap">
                    <span className={`trk-status-badge ${STATUS_BADGE_MAP[result.status]?.cls || "trk-status-badge--appointment_booked"}`}>
                      {STATUS_BADGE_MAP[result.status]?.text || "Application Received"}
                    </span>
                    <button
                      className="trk-scroll-btn"
                      onClick={() => {
                        const el = document.getElementById("reapply-section");
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                    >
                      Re-Apply ↓
                    </button>
                  </span>
                ) : (
                  <span className={`trk-status-badge ${STATUS_BADGE_MAP[result.status]?.cls || "trk-status-badge--appointment_booked"}`}>
                    {STATUS_BADGE_MAP[result.status]?.text || "Application Received"}
                  </span>
                )}
              </div>

              {/* Progress steps */}
              <div className="trk-steps">
                {stepsToShow.map((step, i) => {
                  const isDone   = i < result.stepIndex;
                  const isActive = i === result.stepIndex;
                  return (
                    <div key={i} className={`trk-step ${isDone ? "trk-step--done" : ""} ${isActive ? "trk-step--active" : ""}`}>
                      {i < stepsToShow.length - 1 && (
                        <div className={`trk-step-line ${isDone ? "trk-step-line--done" : ""}`} />
                      )}
                      <div className="trk-step-dot">{isDone ? "✓" : i + 1}</div>
                      <p className="trk-step-label">{step.label}</p>
                    </div>
                  );
                })}
              </div>

              {/* Detail cards */}
              <div className="trk-details">
                <div className="trk-detail-card trk-detail-card--blue">
                  <h4>Appointment Details</h4>
                  <div className="trk-detail-rows">
                    <div className="trk-detail-row"><span>Date</span><strong>{result.appointmentDate}</strong></div>
                    <div className="trk-detail-row"><span>Time</span><strong>{result.appointmentTime}</strong></div>
                    <div className="trk-detail-row"><span>Center</span><strong>{result.appointmentCenter}</strong></div>
                  </div>
                  {result.stepIndex <= 2 && (
                    <p className="trk-reminder">
                      Please carry all original documents and arrive 15 minutes early. Missing your appointment requires a re-application.
                    </p>
                  )}
                </div>

                <div className="trk-detail-card trk-detail-card--green">
                  <h4>Application Filing Status</h4>
                  <div className="trk-detail-rows">
                    <div className="trk-detail-row"><span>Status</span><strong>{result.filingStatus}</strong></div>
                    <div className="trk-detail-row"><span>Filed On</span><strong>{result.filedAt}</strong></div>
                    <div className="trk-detail-row"><span>Application ID</span><strong>{result.applicationId}</strong></div>
                    <div className="trk-detail-row"><span>Submitted On</span><strong>{result.submittedAt}</strong></div>
                    {isReapplication && result.reapplyId && (
                      <div className="trk-detail-row"><span>Re-Application ID</span><strong>{result.reapplyId}</strong></div>
                    )}
                  </div>
                </div>

                <div className="trk-detail-card trk-detail-card--purple">
                  <h4>Police Verification &amp; Dispatch</h4>
                  <div className="trk-detail-rows">
                    <div className="trk-detail-row"><span>Police Verification</span><strong>{result.policeVerification}</strong></div>
                    <div className="trk-detail-row"><span>Dispatch Status</span><strong>{result.dispatchStatus}</strong></div>
                    {result.passportBuddy && (
                      <div className="trk-detail-row">
                        <span>Passport Buddy</span>
                        <strong>Agent assigned — will contact you 24 hrs before appointment</strong>
                      </div>
                    )}
                </div>
              </div>

              <div className="trk-detail-card trk-detail-card--applicant">
                <h4>Applicant Details</h4>
                <div className="trk-detail-rows">
                  {result.applicants.map((a, i) => (
                    <div key={i} className="trk-applicant-block">
                      {result.applicants.length > 1 && <div className="trk-applicant-sub">Applicant {i + 1}</div>}
                      <div className="trk-applicant-grid">
                        <div className="trk-detail-row trk-name-row"><span>Name</span><strong style={{ fontWeight: 700, color: "#000" }}>{a.name || [a.firstName, a.lastName].filter(Boolean).join(" ") || "—"}</strong></div>
                        <div className="trk-detail-row"><span>Phone</span><strong>+91 {a.phone}</strong></div>
                        <div className="trk-detail-row"><span>Country</span><strong>{a.country || "India"}</strong></div>
                        <div className="trk-detail-row"><span>Address</span><strong>{a.address || "—"}</strong></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Re-Apply Section */}
              {!isReapplication && result.status !== "dispatched" && (
                <div id="reapply-section">
                  {reapplyStep === 0 && (
                    <button className="pg-btn-primary trk-reapply-btn" onClick={handleOpenReapply}>
                     Re-Application
                    </button>
                  )}

                  {reapplyStep === 1 && renderStep1()}
                  {reapplyStep === 2 && renderConsentStep()}
                  {reapplyStep === 3 && renderPaymentStep()}
                  {reapplyStep === 4 && reapplyData && renderSuccessStep()}
                </div>
              )}

              {result.status === "dispatched" && !isReapplication && (
                <div style={{
                  background: "var(--brand-faint)",
                  border: "1px solid rgba(26,58,92,0.18)",
                  borderRadius: "var(--radius-md)",
                  padding: "16px 20px",
                  marginTop: "16px",
                  textAlign: "center",
                }}>
                  <p style={{ fontFamily: "var(--font-body)", fontSize: "13px", color: "var(--ink-mid)", fontWeight: 300, marginBottom: "12px" }}>
                    Your passport has been dispatched. No re-application needed.
                  </p>
                </div>
              )}

              {isReapplication && (
                <div style={{
                  background: "var(--bg-alt)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-md)",
                  padding: "18px 22px",
                }}>
                  <p style={{ fontFamily: "var(--font-body)", fontSize: "13px", color: "var(--ink-mid)", fontWeight: 300, lineHeight: 1.6 }}>
                    This is an active re-application. For further changes, please contact our support team or{" "}
                    <span
                      style={{ color: "var(--brand)", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => {
                        setTrackInput(result.originalTrackId);
                        const found = findApplication(result.originalTrackId);
                        if (found) { setResult(found); resetReapply(found); }
                      }}
                    >
                      go back to your original application
                    </span>
                    .
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

         {/* <div className="pb-modal-footer">
           <p className="pb-footer-note">Track IDs are valid for the lifetime of your application.</p>
           <div className="pb-footer-actions">
             <button className="pb-cancel-btn" onClick={onClose}>Close</button>
           </div>
         </div> */}
       </div>

       <LoginGateTA
         open={showLoginGateTA}
         onClose={() => setShowLoginGateTA(false)}
         onSuccess={() => {}}
       />
     </div>

   );
 }




